import { useState, useEffect, useRef, useCallback, useMemo } from "react";
import { useNavigate, useLocation, useSearchParams } from "react-router-dom";
import { useAuth, API } from "@/App";
import axios from "axios";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { Calendar as CalendarComponent } from "@/components/ui/calendar";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSeparator,
} from "@/components/ui/dropdown-menu";
import { toast } from "sonner";
import { format, addDays, isSameDay, startOfWeek } from "date-fns";
import { it } from "date-fns/locale";
import ColorBarPro from "@/components/ColorBarPro";
import SuggerimentiSlotPosa from "@/components/SuggerimentiSlotPosa";
import AppuntamentoCard from "@/components/agenda/AppuntamentoCard";
import AgendaSettimanaleView from "@/components/agenda/AgendaSettimanaleView";
import ServiziSelector from "@/components/agenda/ServiziSelector";
import StoricoCliente from "@/components/StoricoCliente";
import { 
  ChevronLeft,
  ChevronRight,
  Plus,
  Clock,
  User,
  GripVertical,
  Minus,
  Trash2,
  Edit3,
  CheckCircle2,
  Banknote,
  MoreVertical,
  CalendarDays,
  Coffee,
  Palmtree,
  UserX,
  Briefcase,
  Eye,
  Wrench,
  GripHorizontal,
  Check,
  X,
  Palette,
  Lightbulb,
  ArrowRightLeft,
  MessageCircle,
  Mail,
  Send,
  FileText,
  Upload,
  Loader2,
  AlertCircle,
  Bell,
  Phone,
  Scissors,
  Hourglass,
  Sparkles,
  History,
  Repeat,
  Search,
  Settings,
  Calendar,
  ArrowRight,
  LayoutGrid,
  List,
  Star,
  Users,
  Share2,
  HelpCircle
} from "lucide-react";

const SLOT_HEIGHT_COMPACT = 11; // Vista compatta - giornata intera
const SLOT_HEIGHT_EXPANDED = 22; // Vista espansa - più dettagli
const SLOT_MINUTES = 5; // Scaglioni da 5 minuti per maggiore precisione

// Genera gli orari slot in base all'apertura e chiusura
const generateOrariSalone = (apertura = "09:00", chiusura = "19:00") => {
  const slots = [];
  const [hStart, mStart] = apertura.split(':').map(Number);
  const [hEnd, mEnd] = chiusura.split(':').map(Number);
  
  let currentMinutes = hStart * 60 + mStart;
  const endMinutes = hEnd * 60 + mEnd;
  
  // Genera slots fino all'ultimo che inizia prima della chiusura
  // L'ultimo slot disponibile per appuntamenti è quello che inizia appena prima della chiusura
  while (currentMinutes < endMinutes) {
    const h = Math.floor(currentMinutes / 60);
    const m = currentMinutes % 60;
    slots.push(`${h.toString().padStart(2, '0')}:${m.toString().padStart(2, '0')}`);
    currentMinutes += SLOT_MINUTES;
  }
  
  // Aggiungi l'orario di chiusura finale per mostrare la griglia completa
  if (slots.length > 0) {
    const lastSlot = slots[slots.length - 1];
    const [lastH, lastM] = lastSlot.split(':').map(Number);
    const lastSlotEnd = lastH * 60 + lastM + SLOT_MINUTES;
    // Se l'ultimo slot + 15 min non arriva alla chiusura, aggiungi altri slot
    while (lastSlotEnd + (slots.length * 0) < endMinutes) {
      break; // Gli slot sono già generati correttamente
    }
  }
  
  return slots;
};

// Mappa giorni da Date.getDay() (0=domenica) a chiave italiana
const GIORNI_MAP = ['domenica', 'lunedi', 'martedi', 'mercoledi', 'giovedi', 'venerdi', 'sabato'];

// Ottiene il giorno della settimana in italiano da una Date
const getGiornoSettimana = (date) => {
  return GIORNI_MAP[date.getDay()];
};

// Tipi di blocco/info operatore
const TIPI_BLOCCO = [
  { id: 'pausa_pranzo', label: 'Pausa Pranzo', icon: Coffee, color: 'bg-amber-400', defaultDurata: 60 },
  { id: 'ferie', label: 'Ferie', icon: Palmtree, color: 'bg-green-400', defaultDurata: 480 },
  { id: 'permesso', label: 'Permesso', icon: UserX, color: 'bg-orange-400', defaultDurata: 240 },
  { id: 'visita', label: 'Visita', icon: Eye, color: 'bg-cyan-400', defaultDurata: 60 },
  { id: 'app_personale', label: 'App. Personale', icon: CalendarDays, color: 'bg-indigo-400', defaultDurata: 60 },
  { id: 'app_servizio', label: 'App. Servizio', icon: Wrench, color: 'bg-rose-400', defaultDurata: 60 },
];

export default function Agenda() {
  const { token } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();
  const [dataCorrente, setDataCorrente] = useState(new Date());
  const [appuntamenti, setAppuntamenti] = useState([]);
  const [blocchiOperatore, setBlocchiOperatore] = useState([]);
  const [clienti, setClienti] = useState([]);
  const [servizi, setServizi] = useState([]);
  const [operatori, setOperatori] = useState([]);
  const [consulenzeStatus, setConsulenzeStatus] = useState({}); // Stato consulenze
  const [marketingReminders, setMarketingReminders] = useState([]); // Reminder marketing
  const [loading, setLoading] = useState(true);
  const [filterCategoria, setFilterCategoria] = useState("donna"); // Default Donna
  const [searchCliente, setSearchCliente] = useState("");
  const [searchOpen, setSearchOpen] = useState(false); // Toggle ricerca mobile
  const [searchTerm, setSearchTerm] = useState(""); // Termine ricerca
  const [highlightAppId, setHighlightAppId] = useState(null); // ID appuntamento da evidenziare
  
  // Operatori visibili (responsive: 1 su mobile, 3 su desktop)
  const [operatoriVisibili, setOperatoriVisibili] = useState([]);
  const [startIndex, setStartIndex] = useState(0);
  const [numOperatoriVisibili, setNumOperatoriVisibili] = useState(3);
  const [isMobile, setIsMobile] = useState(false);
  
  // Rileva dimensione schermo
  useEffect(() => {
    const checkMobile = () => {
      const mobile = window.innerWidth < 768;
      setIsMobile(mobile);
      // Su mobile SEMPRE 1 operatore per evitare scroll orizzontale
      if (mobile) {
        setNumOperatoriVisibili(1);
      }
    };
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);
  
  // Drag state per spostare appuntamenti tra operatori
  const [draggingApp, setDraggingApp] = useState(null);
  const [dragOverOperatore, setDragOverOperatore] = useState(null);
  
  // Conferma spostamento operatore
  const [confirmMoveOpen, setConfirmMoveOpen] = useState(false);
  const [pendingMove, setPendingMove] = useState(null);
  
  // Calendario mese
  const [calendarOpen, setCalendarOpen] = useState(false);
  
  // Toggle vista compatta/espansa
  const [vistaCompatta, setVistaCompatta] = useState(true);
  const [modalitaVista, setModalitaVista] = useState('giorno'); // 'giorno' | 'settimana'
  const SLOT_HEIGHT = vistaCompatta ? SLOT_HEIGHT_COMPACT : SLOT_HEIGHT_EXPANDED;
  
  // Dialog sposta appuntamento a altro giorno
  const [spostaAppOpen, setSpostaAppOpen] = useState(false);
  const [appDaSpostare, setAppDaSpostare] = useState(null);
  const [spostaFormData, setSpostaFormData] = useState({
    nuovaData: null,
    nuovaOra: "",
    nuovoOperatoreId: "",
    serviziModificati: []
  });
  
  // Resize state
  const [resizingApp, setResizingApp] = useState(null);
  const [dragStartY, setDragStartY] = useState(0);
  const [originalDurata, setOriginalDurata] = useState(0);
  
  // Vertical drag state (spostamento orario)
  const [verticalDragApp, setVerticalDragApp] = useState(null);
  const [verticalDragStartY, setVerticalDragStartY] = useState(0);
  const [originalOra, setOriginalOra] = useState("");
  
  // Refs per drag/resize sicuro (previene removeChild errors)
  const dragStateRef = useRef({
    isResizing: false,
    isVerticalDrag: false,
    appId: null,
    startY: 0,
    originalValue: null
  });
  const appuntamentiRef = useRef([]);
  
  // Quick edit dialog
  const [quickEditApp, setQuickEditApp] = useState(null);
  const [quickEditOpen, setQuickEditOpen] = useState(false);
  const [addServizioOpen, setAddServizioOpen] = useState(false);
  const [searchServizioAgenda, setSearchServizioAgenda] = useState("");
  const [deleteRicorrenteOpen, setDeleteRicorrenteOpen] = useState(false); // Dialog eliminazione ricorrente
  
  // Ricerca cliente rapida
  const [clienteSearchOpen, setClienteSearchOpen] = useState(false);
  const [clienteSearchQuery, setClienteSearchQuery] = useState("");
  const [clienteSearchResults, setClienteSearchResults] = useState([]);
  const [clienteAppuntamenti, setClienteAppuntamenti] = useState({ futuri: [], passati: [], ricorrenti: [] });
  const [clienteSelezionato, setClienteSelezionato] = useState(null);
  const [loadingClienteApp, setLoadingClienteApp] = useState(false);
  
  // Dialog info posa
  const [posaInfoOpen, setPosaInfoOpen] = useState(false);
  const [posaInfoData, setPosaInfoData] = useState(null);
  
  // Storico Cliente
  const [storicoOpen, setStoricoOpen] = useState(false);
  const [storicoClienteId, setStoricoClienteId] = useState(null);
  const [storicoClienteNome, setStoricoClienteNome] = useState('');
  
  // Dialog nuovo appuntamento
  const [dialogOpen, setDialogOpen] = useState(false);
  const [isEditing, setIsEditing] = useState(false); // True se stiamo modificando un appuntamento esistente
  const [selectedSlot, setSelectedSlot] = useState(null);
  const [nuovoClienteOpen, setNuovoClienteOpen] = useState(false);
  const [nuovoCliente, setNuovoCliente] = useState({ nome: '', cognome: '', telefono: '', genere: '' });
  const [isFromSlotPosa, setIsFromSlotPosa] = useState(false); // Flag per appuntamenti da slot posa
  const [formData, setFormData] = useState({
    cliente_id: "",
    cliente_nome: "",
    data: "",
    ora: "",
    servizi: [],
    note: "",
    operatore_id: "",
    operatore_nome: "",
    is_slot_posa: false, // Flag per colorare diversamente
    ricorrente: false,
    ricorrenza_tipo: null,
    ricorrenza_fine: null,
    cliente_nuovo: false, // Stellina cliente nuovo
    canale_acquisizione: null // passaparola, social, google, volantino, altro
  });

  // Funzione per resettare il form
  const resetForm = () => {
    setFormData({
      cliente_id: "",
      cliente_nome: "",
      data: format(dataCorrente, 'yyyy-MM-dd'),
      ora: "",
      servizi: [],
      note: "",
      operatore_id: "",
      operatore_nome: "",
      is_slot_posa: false,
      ricorrente: false,
      ricorrenza_tipo: null,
      ricorrenza_fine: null,
      cliente_nuovo: false,
      canale_acquisizione: null
    });
    setSearchCliente("");
    setIsFromSlotPosa(false);
  };

  // Color Bar
  const [colorBarOpen, setColorBarOpen] = useState(false);
  const [colorBarAppuntamento, setColorBarAppuntamento] = useState(null);
  const [colorBarCliente, setColorBarCliente] = useState(null);
  const [configurazione, setConfigurazione] = useState(null);
  
  // Orari dinamici del salone per il giorno corrente
  const [orariGiorno, setOrariGiorno] = useState({
    attivo: true,
    apertura: "09:00",
    chiusura: "19:00",
    // RIMOSSO: pausa salone globale - ogni operatore gestisce la propria pausa nei turni
    pausa_inizio: null,
    pausa_fine: null
  });
  const [saloneChiuso, setSaloneChiuso] = useState(false);

  // Dialog blocco operatore
  const [bloccoDialogOpen, setBloccoDialogOpen] = useState(false);
  const [bloccoData, setBloccoData] = useState({
    operatore_id: "",
    tipo: "",
    ora_inizio: "13:00",
    ora_fine: "14:00",
    note: ""
  });
  
  // Stato invio promemoria
  const [invioPromemoriaLoading, setInvioPromemoriaLoading] = useState(false);

  // Dialog Consulenza rapida
  const [consulenzaDialogOpen, setConsulenzaDialogOpen] = useState(false);
  const [consulenzaCliente, setConsulenzaCliente] = useState(null);
  const [consulenzaFile, setConsulenzaFile] = useState(null);
  const [consulenzaDescrizione, setConsulenzaDescrizione] = useState("");
  const [consulenzaUploading, setConsulenzaUploading] = useState(false);

  const gridRef = useRef(null);

  // ==================== MEMOIZED COMPUTATIONS ====================
  // Ottimizzazione performance: calcola una volta sola quando cambiano i dati
  
  const operatoriAttivi = useMemo(() => {
    if (!operatori || !Array.isArray(operatori)) return [];
    return operatori.filter(o => o && o.attivo !== false);
  }, [operatori]);

  const appuntamentiPerOperatore = useMemo(() => {
    const map = {};
    if (!appuntamenti || !Array.isArray(appuntamenti)) return map;
    
    appuntamenti.forEach(a => {
      if (a && a.stato !== 'cancellato' && a.operatore_id) {
        if (!map[a.operatore_id]) map[a.operatore_id] = [];
        map[a.operatore_id].push(a);
      }
    });
    return map;
  }, [appuntamenti]);

  const blocchiPerOperatore = useMemo(() => {
    const map = {};
    if (!blocchiOperatore || !Array.isArray(blocchiOperatore)) return map;
    blocchiOperatore.forEach(b => {
      if (b && b.operatore_id) {
        if (!map[b.operatore_id]) map[b.operatore_id] = [];
        map[b.operatore_id].push(b);
      }
    });
    return map;
  }, [blocchiOperatore]);

  // Clienti filtrati con ricerca ottimizzata
  const clientiFiltrati = useMemo(() => {
    if (!searchCliente || searchCliente.length < 2) return [];
    const search = searchCliente.toLowerCase().trim();
    return clienti
      .filter(c => {
        const fullName = `${c.nome} ${c.cognome}`.toLowerCase();
        const telefono = c.telefono || '';
        return fullName.includes(search) || telefono.includes(search);
      })
      .slice(0, 10);
  }, [clienti, searchCliente]);

  // ==================== EFFECTS ====================

  useEffect(() => {
    fetchInitialData();
  }, []);

  // Gestisci parametro prenotaCliente da navigazione (es. da pagina Clienti)
  useEffect(() => {
    if (location.state?.prenotaCliente) {
      const { id, nome } = location.state.prenotaCliente;
      setFormData(prev => ({
        ...prev,
        cliente_id: id,
        cliente_nome: nome,
        data: format(dataCorrente, 'yyyy-MM-dd')
      }));
      setDialogOpen(true);
      // Pulisci lo state per evitare che si riapra
      navigate(location.pathname, { replace: true, state: {} });
      return;
    }

    // Ripristina form dialog se l'utente torna dal profilo cliente con dati in volo
    let pending = location.state?.restorePendingAppuntamento;
    if (!pending) {
      try {
        const saved = sessionStorage.getItem('pendingAppuntamento');
        if (saved && (searchParams.get('restoreForm') === '1' || location.state?.restoreFromCliente)) {
          pending = JSON.parse(saved);
        }
      } catch (e) { console.warn('sessionStorage non disponibile:', e); }
    }
    if (pending && pending.cliente_id) {
      setFormData(prev => ({ ...prev, ...pending }));
      setIsEditing(!!pending.id);
      setDialogOpen(true);
      try { sessionStorage.removeItem('pendingAppuntamento'); } catch (e) { console.warn('sessionStorage:', e); }
      // Pulisci query string e state
      if (searchParams.get('restoreForm')) {
        searchParams.delete('restoreForm');
        setSearchParams(searchParams, { replace: true });
      }
    }
  }, [location.state]);

  // Gestisci parametri URL per tornare al giorno giusto (da navigazione Clienti)
  useEffect(() => {
    const dataParam = searchParams.get('data');
    const highlightParam = searchParams.get('highlight');
    
    if (dataParam) {
      try {
        const nuovaData = new Date(dataParam);
        if (!isNaN(nuovaData.getTime())) {
          setDataCorrente(nuovaData);
        }
      } catch (e) {
        console.error('Data non valida:', dataParam);
      }
    }
    
    if (highlightParam) {
      setHighlightAppId(highlightParam);
      // Scroll all'appuntamento dopo un breve delay
      setTimeout(() => {
        const element = document.querySelector(`[data-appuntamento-id="${highlightParam}"]`);
        if (element) {
          element.scrollIntoView({ behavior: 'smooth', block: 'center' });
          element.classList.add('ring-4', 'ring-purple-500', 'ring-offset-2', 'animate-pulse');
          setTimeout(() => {
            element.classList.remove('ring-4', 'ring-purple-500', 'ring-offset-2', 'animate-pulse');
            setHighlightAppId(null);
          }, 3000);
        }
      }, 500);
    }
    
    // Rimuovi i parametri dall'URL
    if (dataParam || highlightParam) {
      setSearchParams({}, { replace: true });
    }
  }, [searchParams]);

  useEffect(() => {
    if (operatoriAttivi.length > 0) {
      setOperatoriVisibili(operatoriAttivi.slice(startIndex, startIndex + numOperatoriVisibili));
    }
  }, [operatoriAttivi, startIndex, numOperatoriVisibili]);

  useEffect(() => {
    fetchAppuntamenti();
    fetchBlocchiOperatore();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dataCorrente, modalitaVista]);
  
  // Aggiorna orari del salone quando cambia la configurazione o la data
  useEffect(() => {
    if (configurazione && configurazione.orari) {
      const giorno = getGiornoSettimana(dataCorrente);
      const orarioDelGiorno = configurazione.orari[giorno] || {};
      
      // Controlla anche le chiusure straordinarie
      const chiusuraStraordinaria = isChiusuraStraordinaria(dataCorrente);
      
      setSaloneChiuso(orarioDelGiorno.attivo === false || chiusuraStraordinaria !== null);
      setOrariGiorno({
        attivo: orarioDelGiorno.attivo !== false && chiusuraStraordinaria === null,
        apertura: orarioDelGiorno.apertura || "09:00",
        chiusura: orarioDelGiorno.chiusura || "19:00",
        // RIMOSSO: pausa salone - ogni operatore ha la sua pausa individuale nei turni
        pausa_inizio: null,
        pausa_fine: null,
        motivo_chiusura: chiusuraStraordinaria // Aggiungi motivo se c'è chiusura straordinaria
      });
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [configurazione, dataCorrente]);
  
  // Genera gli slot orari dinamici per il giorno
  const ORARI_SALONE = generateOrariSalone(orariGiorno.apertura, orariGiorno.chiusura);
  
  // Funzione per verificare se un operatore è disponibile in un determinato orario
  const isOperatoreDisponibile = (operatore, ora) => {
    if (!operatore.turni || operatore.turni.length === 0) return true; // Nessun turno = sempre disponibile
    
    const giorno = getGiornoSettimana(dataCorrente);
    const turno = operatore.turni.find(t => t.giorno === giorno);
    
    if (!turno || turno.attivo === false) return false; // Operatore non lavora questo giorno
    
    const [h, m] = ora.split(':').map(Number);
    const oraMinuti = h * 60 + m;
    
    // Verifica orario inizio/fine
    const [ih, im] = (turno.inizio || "09:00").split(':').map(Number);
    const [fh, fm] = (turno.fine || "19:00").split(':').map(Number);
    const inizioMinuti = ih * 60 + im;
    const fineMinuti = fh * 60 + fm;
    
    if (oraMinuti < inizioMinuti || oraMinuti >= fineMinuti) return false;
    
    // Verifica pausa operatore
    if (turno.pausa_inizio && turno.pausa_fine) {
      const [pih, pim] = turno.pausa_inizio.split(':').map(Number);
      const [pfh, pfm] = turno.pausa_fine.split(':').map(Number);
      const pausaInizio = pih * 60 + pim;
      const pausaFine = pfh * 60 + pfm;
      if (oraMinuti >= pausaInizio && oraMinuti < pausaFine) return false;
    }
    
    return true;
  };
  
  // Ottieni info turno operatore per il giorno corrente
  const getTurnoOperatore = (operatore) => {
    if (!operatore.turni || operatore.turni.length === 0) return null;
    const giorno = getGiornoSettimana(dataCorrente);
    return operatore.turni.find(t => t.giorno === giorno) || null;
  };
  
  // Verifica se una data è in una chiusura straordinaria del salone
  const isChiusuraStraordinaria = (data) => {
    if (!configurazione?.chiusure_straordinarie) return null;
    const dataStr = format(data, 'yyyy-MM-dd');
    
    for (const chiusura of configurazione.chiusure_straordinarie) {
      if (chiusura.tipo === 'singolo' && chiusura.data === dataStr) {
        return chiusura.motivo || 'Chiusura straordinaria';
      }
      if (chiusura.tipo === 'range' && chiusura.data && chiusura.data_fine) {
        if (dataStr >= chiusura.data && dataStr <= chiusura.data_fine) {
          return chiusura.motivo || 'Chiusura straordinaria';
        }
      }
    }
    return null;
  };
  
  // Verifica se un operatore è in ferie in una data specifica
  const isOperatoreInFerie = (operatore, data) => {
    if (!operatore.ferie || operatore.ferie.length === 0) return null;
    const dataStr = format(data, 'yyyy-MM-dd');
    
    for (const feria of operatore.ferie) {
      if (feria.tipo === 'singolo' && feria.data === dataStr) {
        return feria.motivo || 'Ferie';
      }
      if (feria.tipo === 'range' && feria.data && feria.data_fine) {
        if (dataStr >= feria.data && dataStr <= feria.data_fine) {
          return feria.motivo || 'Ferie';
        }
      }
    }
    return null;
  };
  
  // Motivo chiusura salone (giorno settimanale o straordinaria)
  const motivoChiusuraSalone = isChiusuraStraordinaria(dataCorrente);

  const fetchInitialData = async () => {
    try {
      const [clientiRes, serviziRes, operatoriRes, configRes, consulenzeRes] = await Promise.all([
        axios.get(`${API}/clienti`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/servizi`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/operatori`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/configurazione`, { headers: { Authorization: `Bearer ${token}` } }).catch(() => ({ data: null })),
        axios.get(`${API}/consulenze/status-by-cliente`, { headers: { Authorization: `Bearer ${token}` } }).catch(() => ({ data: {} }))
      ]);
      setClienti(clientiRes.data);
      setServizi(serviziRes.data);
      setOperatori(operatoriRes.data);
      setConfigurazione(configRes.data);
      setConsulenzeStatus(consulenzeRes.data || {});
      
      if (serviziRes.data.length === 0) {
        await axios.post(`${API}/servizi/seed`, {}, { headers: { Authorization: `Bearer ${token}` } });
        const newServizi = await axios.get(`${API}/servizi`, { headers: { Authorization: `Bearer ${token}` } });
        setServizi(newServizi.data);
      }
    } catch (error) {
    }
  };

  // Sincronizza ref con stato appuntamenti per accesso sicuro durante drag
  useEffect(() => {
    appuntamentiRef.current = appuntamenti;
  }, [appuntamenti]);

  const fetchAppuntamenti = async () => {
    setLoading(true);
    try {
      const dataStr = format(dataCorrente, 'yyyy-MM-dd');
      // Se vista settimanale, carica tutta la settimana (lunedì-sabato)
      const rangeParams = (() => {
        if (modalitaVista === 'settimana') {
          const inizio = startOfWeek(dataCorrente, { weekStartsOn: 1 });
          const fine = addDays(inizio, 5);
          return `?data_inizio=${format(inizio, 'yyyy-MM-dd')}&data_fine=${format(fine, 'yyyy-MM-dd')}`;
        }
        return `?data=${dataStr}`;
      })();
      const [appRes, remindersRes] = await Promise.all([
        axios.get(`${API}/appuntamenti${rangeParams}`, {
          headers: { Authorization: `Bearer ${token}` }
        }),
        axios.get(`${API}/marketing/reminders?data=${dataStr}`, {
          headers: { Authorization: `Bearer ${token}` }
        }).catch(() => ({ data: [] }))
      ]);
      setAppuntamenti(appRes.data);
      setMarketingReminders(remindersRes.data || []);
    } catch (error) {
      toast.error("Errore nel caricamento");
    } finally {
      setLoading(false);
    }
  };

  const fetchBlocchiOperatore = async () => {
    try {
      const dataStr = format(dataCorrente, 'yyyy-MM-dd');
      const response = await axios.get(`${API}/blocchi-operatore?data=${dataStr}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setBlocchiOperatore(response.data || []);
    } catch (error) {
      // Endpoint potrebbe non esistere ancora
      setBlocchiOperatore([]);
    }
  };

  const navigateDate = (direction) => {
    setDataCorrente(addDays(dataCorrente, direction));
  };

  const goToToday = () => {
    setDataCorrente(new Date());
  };

  const navigateOperatori = (direction) => {
    const newIndex = startIndex + direction;
    if (newIndex >= 0 && newIndex <= operatoriAttivi.length - numOperatoriVisibili) {
      setStartIndex(newIndex);
    }
  };

  // Converti ora in minuti dall'inizio (9:00)
  // Converte ora "HH:MM" in minuti relativi all'apertura del salone
  const oraToMinuti = (ora) => {
    if (!ora) return 0;
    const [h, m] = ora.split(':').map(Number);
    const [aperturaH] = (orariGiorno.apertura || "08:00").split(':').map(Number);
    return (h - aperturaH) * 60 + m;
  };

  // Converte minuti relativi all'apertura in formato ora "HH:MM"
  const minutiToOra = (minuti) => {
    const [aperturaH] = (orariGiorno.apertura || "08:00").split(':').map(Number);
    const ore = Math.floor(minuti / 60) + aperturaH;
    const min = minuti % 60;
    return `${ore.toString().padStart(2, '0')}:${min.toString().padStart(2, '0')}`;
  };

  // Converte minuti assoluti (da mezzanotte) in formato ora "HH:MM"
  const minutiAssolutoToOra = (minuti) => {
    const ore = Math.floor(minuti / 60);
    const min = minuti % 60;
    return `${ore.toString().padStart(2, '0')}:${min.toString().padStart(2, '0')}`;
  };

  const getTopPosition = (ora) => {
    const minuti = oraToMinuti(ora);
    return (minuti / SLOT_MINUTES) * SLOT_HEIGHT;
  };

  const getHeight = (durata) => {
    return Math.max((durata / SLOT_MINUTES) * SLOT_HEIGHT, SLOT_HEIGHT);
  };

  // ORDINE STANDARD ESECUZIONE SERVIZI IN SALONE
  // Indipendentemente dall'ordine di selezione, i servizi vengono riordinati
  // automaticamente per riflettere il flusso reale di lavoro al salone.
  // Priorità: se il servizio anagrafico ha `ordine_schema > 0`, vince quello.
  // Altrimenti si usa la priorità per `tipo`.
  const PRIORITA_TIPO_SERVIZIO = {
    taglio: 1,
    colore: 2,
    trattamento: 3,
    piega: 4,
    barba: 5,
    altro: 99
  };
  const ordinaServiziSalone = (serviziLista) => {
    if (!Array.isArray(serviziLista) || serviziLista.length <= 1) return serviziLista;
    // Helper: recupera tipo e ordine_schema dall'anagrafica per servizi storici senza questi campi
    const arricchisci = (s) => {
      if (s.tipo) return s;
      const anag = (servizi || []).find(x => x.nome === s.nome);
      if (anag) return { ...s, tipo: anag.tipo, ordine_schema: s.ordine_schema || anag.ordine_schema || 0 };
      return s;
    };
    // Sort stabile (Array.prototype.sort è stabile in ES2019+)
    return [...serviziLista]
      .map((s, idx) => ({ s: arricchisci(s), idx }))
      .sort((a, b) => {
        const ordA = (a.s.ordine_schema && a.s.ordine_schema > 0) ? a.s.ordine_schema : (PRIORITA_TIPO_SERVIZIO[a.s.tipo] ?? 50);
        const ordB = (b.s.ordine_schema && b.s.ordine_schema > 0) ? b.s.ordine_schema : (PRIORITA_TIPO_SERVIZIO[b.s.tipo] ?? 50);
        if (ordA !== ordB) return ordA - ordB;
        return a.idx - b.idx; // mantiene ordine inserimento a parità
      })
      .map(x => x.s);
  };

  // Ottieni appuntamenti per un operatore specifico (usa dati memoizzati)
  const getAppuntamentiForOperatore = useCallback((operatoreId) => {
    return appuntamentiPerOperatore[operatoreId] || [];
  }, [appuntamentiPerOperatore]);

  // Ottieni blocchi per un operatore (usa dati memoizzati)
  const getBlocchiForOperatore = useCallback((operatoreId) => {
    return blocchiPerOperatore[operatoreId] || [];
  }, [blocchiPerOperatore]);

  // Drag & Drop handlers
  const handleDragStart = (e, app) => {
    if (isIncassato(app)) return;
    setDraggingApp(app);
    e.dataTransfer.effectAllowed = 'move';
    e.dataTransfer.setData('text/plain', app.id);
  };

  const handleDragOver = (e, operatoreId) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    setDragOverOperatore(operatoreId);
  };

  const handleDragLeave = () => {
    setDragOverOperatore(null);
  };

  const handleDrop = async (e, operatoreId) => {
    e.preventDefault();
    setDragOverOperatore(null);
    
    if (!draggingApp || draggingApp.operatore_id === operatoreId) {
      setDraggingApp(null);
      return;
    }

    const operatore = operatori.find(o => o.id === operatoreId);
    
    // Chiedi conferma prima di spostare
    setPendingMove({
      appuntamento: draggingApp,
      nuovoOperatoreId: operatoreId,
      nuovoOperatoreNome: operatore ? `${operatore.nome} ${operatore.cognome || ''}`.trim() : ''
    });
    setConfirmMoveOpen(true);
    setDraggingApp(null);
  };

  const confirmMoveAppuntamento = async () => {
    if (!pendingMove) return;
    
    try {
      await axios.put(`${API}/appuntamenti/${pendingMove.appuntamento.id}`, {
        ...pendingMove.appuntamento,
        operatore_id: pendingMove.nuovoOperatoreId,
        operatore_nome: pendingMove.nuovoOperatoreNome
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast.success(`Appuntamento spostato a ${pendingMove.nuovoOperatoreNome || 'altro operatore'}`);
      
      // Silent refresh dopo successo
      try {
        const dataStr = format(dataCorrente, 'yyyy-MM-dd');
        const appRes = await axios.get(`${API}/appuntamenti?data=${dataStr}`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setAppuntamenti(appRes.data);
      } catch {
        // Silently fail
      }
    } catch (error) {
      toast.error("Errore nello spostamento");
    }
    
    setPendingMove(null);
    setConfirmMoveOpen(false);
  };

  const cancelMoveAppuntamento = () => {
    setPendingMove(null);
    setConfirmMoveOpen(false);
  };

  // Apri dialog per spostare appuntamento a altro giorno
  const openSpostaAppuntamento = (app) => {
    setAppDaSpostare(app);
    setSpostaFormData({
      nuovaData: new Date(app.data),
      nuovaOra: app.ora,
      nuovoOperatoreId: app.operatore_id,
      serviziModificati: app.servizi || []
    });
    setSpostaAppOpen(true);
  };

  // Salva spostamento appuntamento
  const salvaSpostaAppuntamento = async () => {
    if (!appDaSpostare || !spostaFormData.nuovaData) return;
    
    // Verifica se il giorno selezionato è un giorno di chiusura
    if (configurazione && configurazione.orari) {
      const giorno = getGiornoSettimana(spostaFormData.nuovaData);
      const orarioGiorno = configurazione.orari[giorno];
      if (orarioGiorno && orarioGiorno.attivo === false) {
        toast.error(`${format(spostaFormData.nuovaData, "EEEE", { locale: it })} è un giorno di chiusura del salone`);
        return;
      }
    }
    
    try {
      const nuovaDataStr = format(spostaFormData.nuovaData, 'yyyy-MM-dd');
      const operatore = operatori.find(o => o.id === spostaFormData.nuovoOperatoreId);
      
      // Verifica se l'operatore lavora quel giorno
      if (operatore && operatore.turni) {
        const giorno = getGiornoSettimana(spostaFormData.nuovaData);
        const turno = operatore.turni.find(t => t.giorno === giorno);
        if (turno && turno.attivo === false) {
          toast.error(`${operatore.nome} non lavora ${format(spostaFormData.nuovaData, "EEEE", { locale: it })}`);
          return;
        }
      }
      
      // Calcola nuova ora fine
      const durataTotale = spostaFormData.serviziModificati.reduce((acc, s) => acc + (s.durata_minuti || 30), 0);
      const [h, m] = spostaFormData.nuovaOra.split(':').map(Number);
      const minFine = h * 60 + m + durataTotale;
      const nuovaOraFine = `${Math.floor(minFine / 60).toString().padStart(2, '0')}:${(minFine % 60).toString().padStart(2, '0')}`;
      
      await axios.put(`${API}/appuntamenti/${appDaSpostare.id}`, {
        ...appDaSpostare,
        data: nuovaDataStr,
        ora: spostaFormData.nuovaOra,
        ora_fine: nuovaOraFine,
        operatore_id: spostaFormData.nuovoOperatoreId,
        operatore_nome: operatore ? `${operatore.nome} ${operatore.cognome || ''}`.trim() : appDaSpostare.operatore_nome,
        servizi: spostaFormData.serviziModificati,
        durata_totale: durataTotale
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast.success(`Appuntamento spostato al ${format(spostaFormData.nuovaData, 'd MMMM', { locale: it })}`);
      
      // Chiudi il dialog PRIMA di aggiornare lo stato
      setSpostaAppOpen(false);
      setAppDaSpostare(null);
      
      // Se la data è cambiata, aggiorna la vista con delay
      const dataChanged = nuovaDataStr !== format(dataCorrente, 'yyyy-MM-dd');
      
      // Aggiorna appuntamenti con delay per evitare conflitti DOM
      setTimeout(async () => {
        if (dataChanged) {
          setDataCorrente(spostaFormData.nuovaData);
        }
        
        try {
          const dataStr = format(dataChanged ? spostaFormData.nuovaData : dataCorrente, 'yyyy-MM-dd');
          const appRes = await axios.get(`${API}/appuntamenti?data=${dataStr}`, {
            headers: { Authorization: `Bearer ${token}` }
          });
          setAppuntamenti(appRes.data);
        } catch {
          // Silently fail
        }
      }, 150);
    } catch (error) {
      toast.error("Errore nello spostamento");
    }
  };

  // Aggiungi servizio nel form spostamento
  const addServizioSpostamento = (servizio) => {
    if (spostaFormData.serviziModificati.find(s => s.id === servizio.id)) return;
    setSpostaFormData({
      ...spostaFormData,
      serviziModificati: [...spostaFormData.serviziModificati, {
        id: servizio.id,
        nome: servizio.nome,
        prezzo: servizio.prezzo,
        durata_minuti: servizio.durata_minuti,
        tempo_lavoro: servizio.tempo_lavoro || 0,
        tempo_posa: servizio.tempo_posa || 0,
        colore: servizio.colore || '#3B82F6'
      }]
    });
  };

  // Rimuovi servizio dal form spostamento
  const removeServizioSpostamento = (servizioId) => {
    setSpostaFormData({
      ...spostaFormData,
      serviziModificati: spostaFormData.serviziModificati.filter(s => s.id !== servizioId)
    });
  };

  // Resize handlers - usando refs per evitare stale closures e removeChild errors
  const handleResizeStart = (e, app) => {
    e.stopPropagation();
    e.preventDefault();
    if (isIncassato(app)) return;
    
    // Salva stato nel ref per accesso stabile
    dragStateRef.current = {
      isResizing: true,
      isVerticalDrag: false,
      appId: app.id,
      startY: e.clientY,
      originalValue: app.durata_totale || 30
    };
    
    setResizingApp(app);
    setDragStartY(e.clientY);
    setOriginalDurata(app.durata_totale || 30);
    
    // Usa funzioni stabili che leggono dal ref
    document.addEventListener('mousemove', stableResizeMove);
    document.addEventListener('mouseup', stableResizeEnd);
  };

  // ==================== VERTICAL DRAG (Spostamento orario) ====================
  // Funziona SEMPRE per spostare l'appuntamento su/giù nella stessa colonna operatore
  const handleVerticalDragStart = (e, app) => {
    e.stopPropagation();
    e.preventDefault();
    // Solo controllo: non incassato, non esterno
    if (isIncassato(app) || app.is_external) return;
    
    // Salva stato nel ref per accesso stabile
    dragStateRef.current = {
      isResizing: false,
      isVerticalDrag: true,
      appId: app.id,
      startY: e.clientY,
      originalValue: app.ora,
      durataTotale: app.durata_totale || 30
    };
    
    setVerticalDragApp(app);
    setVerticalDragStartY(e.clientY);
    setOriginalOra(app.ora);
    
    // Aggiungi classe per disabilitare selezione testo durante drag
    document.body.style.userSelect = 'none';
    document.body.style.cursor = 'grabbing';
    
    // Usa funzioni stabili che leggono dal ref
    document.addEventListener('mousemove', stableVerticalDragMove);
    document.addEventListener('mouseup', stableVerticalDragEnd);
  };

  // Funzioni stabili per vertical drag (usano refs, non dipendono da stato React)
  const stableVerticalDragMove = useCallback((e) => {
    const state = dragStateRef.current;
    if (!state.isVerticalDrag || !state.appId) return;
    
    const deltaY = e.clientY - state.startY;
    const deltaSlots = Math.round(deltaY / SLOT_HEIGHT);
    const deltaMinutes = deltaSlots * SLOT_MINUTES;
    
    // Calcola nuova ora
    const [h, m] = (state.originalValue || "09:00").split(':').map(Number);
    const originalMinutes = h * 60 + m;
    let newMinutes = originalMinutes + deltaMinutes;
    
    // Limita agli orari del salone
    const [hStart] = ORARI_SALONE[0]?.split(':').map(Number) || [9, 0];
    const [hEnd] = ORARI_SALONE[ORARI_SALONE.length - 1]?.split(':').map(Number) || [19, 0];
    const minMinutes = hStart * 60;
    const maxMinutes = hEnd * 60 - (state.durataTotale || 30);
    
    newMinutes = Math.max(minMinutes, Math.min(maxMinutes, newMinutes));
    newMinutes = Math.round(newMinutes / SLOT_MINUTES) * SLOT_MINUTES;
    
    const newOra = minutiAssolutoToOra(newMinutes);
    const newOraFine = minutiAssolutoToOra(newMinutes + (state.durataTotale || 30));
    
    // Usa requestAnimationFrame per evitare conflitti DOM
    requestAnimationFrame(() => {
      setAppuntamenti(prev => prev.map(a => 
        a.id === state.appId 
          ? { ...a, ora: newOra, ora_fine: newOraFine }
          : a
      ));
    });
  }, [SLOT_HEIGHT, ORARI_SALONE]);

  const stableVerticalDragEnd = useCallback(async () => {
    const state = dragStateRef.current;
    
    // Ripristina stili SUBITO
    document.body.style.userSelect = '';
    document.body.style.cursor = '';
    
    // Rimuovi listeners PRIMA di tutto per evitare conflitti
    document.removeEventListener('mousemove', stableVerticalDragMove);
    document.removeEventListener('mouseup', stableVerticalDragEnd);
    
    if (state.isVerticalDrag && state.appId) {
      const app = appuntamentiRef.current.find(a => a.id === state.appId);
      if (app && app.ora !== state.originalValue) {
        // Chiedi conferma prima di salvare
        const conferma = window.confirm(`Spostare l'appuntamento di ${app.cliente_nome} dalle ${state.originalValue} alle ${app.ora}?`);
        
        if (conferma) {
          try {
            await axios.put(`${API}/appuntamenti/${app.id}`, {
              ...app,
              ora: app.ora,
              ora_fine: app.ora_fine
            }, {
              headers: { Authorization: `Bearer ${token}` }
            });
            toast.success(`✅ Spostato alle ${app.ora}`);
          } catch (error) {
            // Ripristina con delay
            setTimeout(() => {
              setAppuntamenti(prev => prev.map(a => 
                a.id === state.appId ? { ...a, ora: state.originalValue } : a
              ));
            }, 50);
            toast.error("Errore nell'aggiornamento orario");
          }
        } else {
          // Utente ha annullato - ripristina con delay
          const [h, m] = (state.originalValue || "09:00").split(':').map(Number);
          const originalMinutes = h * 60 + m;
          const originalOraFine = minutiAssolutoToOra(originalMinutes + (state.durataTotale || 30));
          
          setTimeout(() => {
            setAppuntamenti(prev => prev.map(a => 
              a.id === state.appId ? { ...a, ora: state.originalValue, ora_fine: originalOraFine } : a
            ));
          }, 50);
          toast.info("Spostamento annullato");
        }
      }
    }
    
    // Reset stato con delay per permettere a React di completare il render
    setTimeout(() => {
      dragStateRef.current = { isResizing: false, isVerticalDrag: false, appId: null, startY: 0, originalValue: null };
      setVerticalDragApp(null);
    }, 50);
  }, [token, stableVerticalDragMove]);

  const handleVerticalDragMove = useCallback((e) => {
    if (!verticalDragApp) return;
    
    const deltaY = e.clientY - verticalDragStartY;
    const deltaSlots = Math.round(deltaY / SLOT_HEIGHT);
    const deltaMinutes = deltaSlots * SLOT_MINUTES;
    
    // Calcola nuova ora
    const [h, m] = originalOra.split(':').map(Number);
    const originalMinutes = h * 60 + m;
    let newMinutes = originalMinutes + deltaMinutes;
    
    // Limita agli orari del salone
    const [hStart] = ORARI_SALONE[0]?.split(':').map(Number) || [9, 0];
    const [hEnd] = ORARI_SALONE[ORARI_SALONE.length - 1]?.split(':').map(Number) || [19, 0];
    const minMinutes = hStart * 60;
    
    // Il massimo è fine giornata meno la durata totale dell'appuntamento
    const maxMinutes = hEnd * 60 - (verticalDragApp.durata_totale || 30);
    
    newMinutes = Math.max(minMinutes, Math.min(maxMinutes, newMinutes));
    
    // Arrotonda a slot da 5 minuti (SLOT_MINUTES)
    newMinutes = Math.round(newMinutes / SLOT_MINUTES) * SLOT_MINUTES;
    
    const newOra = minutiAssolutoToOra(newMinutes);
    const durataTotale = verticalDragApp.durata_totale || 30;
    const newOraFine = minutiAssolutoToOra(newMinutes + durataTotale);
    
    // Aggiorna visivamente
    setAppuntamenti(prev => prev.map(a => 
      a.id === verticalDragApp.id 
        ? { ...a, ora: newOra, ora_fine: newOraFine }
        : a
    ));
  }, [verticalDragApp, verticalDragStartY, originalOra, SLOT_HEIGHT, ORARI_SALONE, SLOT_MINUTES]);

  const handleVerticalDragEnd = useCallback(async () => {
    // Ripristina stili
    document.body.style.userSelect = '';
    document.body.style.cursor = '';
    
    // Rimuovi event listeners PRIMA di tutto
    document.removeEventListener('mousemove', handleVerticalDragMove);
    document.removeEventListener('mouseup', handleVerticalDragEnd);
    
    if (verticalDragApp) {
      const app = appuntamenti.find(a => a.id === verticalDragApp.id);
      if (app && app.ora !== originalOra) {
        // Chiedi conferma prima di salvare
        const conferma = window.confirm(`Spostare l'appuntamento di ${app.cliente_nome} dalle ${originalOra} alle ${app.ora}?`);
        
        if (conferma) {
          try {
            // Salva nuovo orario
            await axios.put(`${API}/appuntamenti/${app.id}`, {
              ...app,
              ora: app.ora,
              ora_fine: app.ora_fine
            }, {
              headers: { Authorization: `Bearer ${token}` }
            });
            
            toast.success(`✅ Spostato alle ${app.ora}`);
          } catch (error) {
            // Ripristina orario originale
            setAppuntamenti(prev => prev.map(a => 
              a.id === verticalDragApp.id ? { ...a, ora: originalOra } : a
            ));
            toast.error("Errore nell'aggiornamento orario");
          }
        } else {
          // Utente ha annullato - ripristina orario originale
          const durataTotale = verticalDragApp.durata_totale || 30;
          const [h, m] = originalOra.split(':').map(Number);
          const originalMinutes = h * 60 + m;
          const originalOraFine = minutiAssolutoToOra(originalMinutes + durataTotale);
          
          setAppuntamenti(prev => prev.map(a => 
            a.id === verticalDragApp.id ? { ...a, ora: originalOra, ora_fine: originalOraFine } : a
          ));
          toast.info("Spostamento annullato");
        }
      }
    }
    
    setVerticalDragApp(null);
  }, [verticalDragApp, appuntamenti, originalOra, token, handleVerticalDragMove, minutiAssolutoToOra]);

  // Toggle fissato appuntamento
  // LOGICA: 
  // - fissato = true o undefined/null = BLOCCATO (rosso, default)
  // - fissato = false = SBLOCCATO (verde, spostabile verticalmente per 5 min)
  const handleToggleFissato = async (appuntamentoId) => {
    try {
      const response = await axios.put(`${API}/appuntamenti/${appuntamentoId}/fissato`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      // Aggiorna lo stato locale
      setAppuntamenti(prev => prev.map(a => 
        a.id === appuntamentoId 
          ? { ...a, fissato: response.data.fissato }
          : a
      ));
      
      // NUOVA LOGICA: fissato=false significa SBLOCCATO (verde)
      // fissato=true o null/undefined significa BLOCCATO (rosso)
      const isSbloccato = response.data.fissato === false;
      toast.success(isSbloccato 
        ? "🟢 SBLOCCATO! Trascina su/giù per cambiare orario" 
        : "🔴 BLOCCATO! Trascina dx/sx per cambiare operatore");
    } catch (error) {
      toast.error("Errore nel cambiare stato");
    }
  };

  // Calcola gli slot di posa per un operatore.
  // NUOVO MODELLO: una posa per servizio, sequenziale (lavoro1 -> posa1 -> lavoro2 -> posa2 ...)
  const getSlotPosaForOperatore = (operatoreId) => {
    const opApps = appuntamenti.filter(a => a.operatore_id === operatoreId);
    const slots = [];
    
    opApps.forEach(app => {
      if (!app.ora) return;
      const [h, m] = app.ora.split(':').map(Number);
      let cursore = h * 60 + m;  // minuto corrente nel timeline
      
      (app.servizi || []).forEach((servizio, idx) => {
        const steps = Array.isArray(servizio.steps) ? servizio.steps : [];
        const hasSteps = steps.length > 0 && steps.some(s => (s.durata_minuti || 0) > 0);

        if (hasSteps) {
          // Modello MULTI-STEP: ogni step di posa diventa uno slot indipendente
          steps.forEach((step, stepIdx) => {
            const tipo = step.tipo === 'posa' ? 'posa' : 'lavoro';
            const dur = step.durata_minuti || 0;
            if (dur <= 0) return;
            if (tipo === 'posa') {
              const inizioPosa = cursore;
              const finePosa = cursore + dur;
              slots.push({
                appuntamentoId: app.id,
                servizioIndex: idx,
                stepIndex: stepIdx,
                servizioNome: servizio.nome,
                stepNome: step.nome || 'Posa',
                clienteNome: app.cliente_nome,
                oraInizioPosa: minutiAssolutoToOra(inizioPosa),
                oraFinePosa: minutiAssolutoToOra(finePosa),
                inizioMinuti: inizioPosa,
                fineMinuti: finePosa,
                durataPosa: dur,
                operatoreId: operatoreId,
                servizi: app.servizi
              });
              cursore = finePosa;
            } else {
              cursore += dur;
            }
          });
          return;
        }

        // Modello CLASSICO
        const tPosa = servizio.tempo_posa || 0;
        const durata = servizio.durata_minuti || 30;
        let tLavoro = servizio.tempo_lavoro || 0;
        if (tLavoro === 0) {
          tLavoro = tPosa > 0 ? Math.max(durata - tPosa, 0) : durata;
        }
        cursore += tLavoro;
        if (tPosa > 0) {
          const inizioPosa = cursore;
          const finePosa = cursore + tPosa;
          slots.push({
            appuntamentoId: app.id,
            servizioIndex: idx,
            servizioNome: servizio.nome,
            clienteNome: app.cliente_nome,
            oraInizioPosa: minutiAssolutoToOra(inizioPosa),
            oraFinePosa: minutiAssolutoToOra(finePosa),
            inizioMinuti: inizioPosa,
            fineMinuti: finePosa,
            durataPosa: tPosa,
            operatoreId: operatoreId,
            servizi: app.servizi
          });
          cursore = finePosa;
        }
      });
    });
    
    return slots;
  };

  // Verifica se un appuntamento si sovrappone a uno slot di posa
  const getOverlappingPosa = (app, slotsPosa) => {
    if (!app.ora) return null;
    const [h, m] = app.ora.split(':').map(Number);
    const appInizio = h * 60 + m;
    const appFine = appInizio + (app.durata_totale || 30);
    
    for (const posa of slotsPosa) {
      // Esclude la posa dello stesso appuntamento
      if (posa.appuntamentoId === app.id) continue;
      
      // Verifica sovrapposizione
      const posaInizio = posa.inizioMinuti;
      const posaFine = posa.fineMinuti;
      
      if (appInizio < posaFine && appFine > posaInizio) {
        return posa;
      }
    }
    return null;
  };

  // NUOVO: Calcola i segmenti di LAVORO di un appuntamento in sequenza temporale.
  // Per ogni servizio crea un segmento di lavoro; tra un servizio e l'altro, se c'è
  // tempo_posa, il cursore viene incrementato (la posa viene renderizzata come slot
  // viola separato). In questo modo: lavoro1 -> posa1 -> lavoro2 -> posa2 -> lavoro3.
  const getSegmentiAppuntamento = (app) => {
    if (!app.ora) return [];
    const [h, m] = app.ora.split(':').map(Number);
    let cursore = h * 60 + m;
    const segmenti = [];
    const servizi = app.servizi || [];

    servizi.forEach((servizio, idx) => {
      const steps = Array.isArray(servizio.steps) ? servizio.steps : [];
      const hasSteps = steps.length > 0 && steps.some(s => (s.durata_minuti || 0) > 0);

      if (hasSteps) {
        // Modello MULTI-STEP: ogni step di lavoro = segmento; ogni step di posa avanza solo il cursore
        steps.forEach((step, stepIdx) => {
          const tipo = step.tipo === 'posa' ? 'posa' : 'lavoro';
          const dur = step.durata_minuti || 0;
          if (dur <= 0) return;
          if (tipo === 'lavoro') {
            segmenti.push({
              servizio,
              servizioIndex: idx,
              stepIndex: stepIdx,
              stepNome: step.nome || servizio.nome,
              inizioMinuti: cursore,
              durata: dur,
              oraInizio: minutiAssolutoToOra(cursore),
              oraFine: minutiAssolutoToOra(cursore + dur)
            });
            cursore += dur;
          } else {
            cursore += dur;
          }
        });
        return;
      }

      // Modello CLASSICO (retrocompatibilità)
      const tPosa = servizio.tempo_posa || 0;
      const durata = servizio.durata_minuti || 30;
      let tLavoro = servizio.tempo_lavoro || 0;
      if (tLavoro === 0) {
        tLavoro = tPosa > 0 ? Math.max(durata - tPosa, 0) : durata;
      }

      if (tLavoro > 0) {
        segmenti.push({
          servizio,
          servizioIndex: idx,
          inizioMinuti: cursore,
          durata: tLavoro,
          oraInizio: minutiAssolutoToOra(cursore),
          oraFine: minutiAssolutoToOra(cursore + tLavoro)
        });
        cursore += tLavoro;
      }
      if (tPosa > 0) {
        cursore += tPosa;
      }
    });

    // Fallback: nessun servizio definito -> singolo blocco con durata_totale
    if (segmenti.length === 0) {
      const start = h * 60 + m;
      const dur = app.durata_totale || 30;
      segmenti.push({
        servizio: servizi[0] || null,
        servizioIndex: 0,
        inizioMinuti: start,
        durata: dur,
        oraInizio: app.ora,
        oraFine: minutiAssolutoToOra(start + dur)
      });
    }
    return segmenti;
  };

  // Click su slot posa - mostra info e opzioni
  const handleSlotPosaClick = async (slot) => {
    // Mostra prima il dialog informativo sulla posa
    setPosaInfoData(slot);
    setPosaInfoOpen(true);
  };
  
  // Procedura per aggiungere servizio durante la posa
  const handleAddServizioDurantePosa = async () => {
    if (!posaInfoData) return;
    const slot = posaInfoData;
    
    // Carica servizi suggeriti
    try {
      const response = await axios.get(`${API}/servizi/all`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      const serviziAttivi = response.data.filter(s => s.attivo);
      
      // Trova servizi che entrano nello slot
      const serviziSuggeriti = serviziAttivi.filter(s => {
        const sLavoro = s.tempo_lavoro || s.durata_minuti;
        return sLavoro <= slot.durataPosa && sLavoro > 0;
      }).sort((a, b) => {
        const aLavoro = a.tempo_lavoro || a.durata_minuti;
        const bLavoro = b.tempo_lavoro || b.durata_minuti;
        const aFit = aLavoro === slot.durataPosa;
        const bFit = bLavoro === slot.durataPosa;
        if (aFit && !bFit) return -1;
        if (!aFit && bFit) return 1;
        return bLavoro - aLavoro;
      }).slice(0, 5);
      
      if (serviziSuggeriti.length === 0) {
        toast.info("Nessun servizio compatibile con questo slot");
        return;
      }
      
      // Prendi il primo servizio suggerito (fit perfetto o più lungo)
      const servizio = serviziSuggeriti[0];
      
      setIsFromSlotPosa(true);
      setFormData({
        cliente_id: "",
        cliente_nome: "",
        data: format(dataCorrente, 'yyyy-MM-dd'),
        ora: slot.oraInizioPosa,
        servizi: [{
          nome: servizio.nome,
          prezzo: servizio.prezzo,
          durata_minuti: servizio.durata_minuti,
          tempo_lavoro: servizio.tempo_lavoro || servizio.durata_minuti,
          tempo_posa: 0
        }],
        note: `Slot posa: ${slot.clienteNome}`,
        operatore_id: slot.operatoreId || "",
        operatore_nome: "",
        is_slot_posa: true
      });
      setPosaInfoOpen(false);
      setDialogOpen(true);
      toast.info(`Aggiungi "${servizio.nome}" alle ${slot.oraInizioPosa}`);
    } catch (error) {
      toast.error("Errore nel caricamento servizi");
    }
  };

  // Funzioni stabili per resize (usano refs, non dipendono da stato React)
  const stableResizeMove = useCallback((e) => {
    const state = dragStateRef.current;
    if (!state.isResizing || !state.appId) return;
    
    const deltaY = e.clientY - state.startY;
    const deltaSlots = Math.round(deltaY / SLOT_HEIGHT);
    const newDurata = Math.max(15, state.originalValue + (deltaSlots * SLOT_MINUTES));
    
    // Usa requestAnimationFrame per evitare conflitti DOM
    requestAnimationFrame(() => {
      setAppuntamenti(prev => prev.map(a => 
        a.id === state.appId 
          ? { ...a, durata_totale: newDurata, ora_fine: minutiToOra(oraToMinuti(a.ora) + newDurata) }
          : a
      ));
    });
  }, [SLOT_HEIGHT]);

  const stableResizeEnd = useCallback(async () => {
    const state = dragStateRef.current;
    
    // Rimuovi listeners PRIMA di tutto per evitare conflitti
    document.removeEventListener('mousemove', stableResizeMove);
    document.removeEventListener('mouseup', stableResizeEnd);
    
    if (state.isResizing && state.appId) {
      // Usa il ref per ottenere l'appuntamento aggiornato
      const app = appuntamentiRef.current.find(a => a.id === state.appId);
      if (app && app.durata_totale !== state.originalValue) {
        try {
          await axios.put(`${API}/appuntamenti/${app.id}/durata`, 
            { durata_totale: app.durata_totale },
            { headers: { Authorization: `Bearer ${token}` } }
          );
          toast.success(`Durata aggiornata: ${app.durata_totale} min`);
        } catch (error) {
          // Ripristina con delay per evitare conflitti DOM
          setTimeout(() => {
            setAppuntamenti(prev => prev.map(a => 
              a.id === state.appId ? { ...a, durata_totale: state.originalValue } : a
            ));
          }, 50);
          toast.error("Errore nell'aggiornamento");
        }
      }
    }
    
    // Reset stato con delay per permettere a React di completare il render
    setTimeout(() => {
      dragStateRef.current = { isResizing: false, isVerticalDrag: false, appId: null, startY: 0, originalValue: null };
      setResizingApp(null);
    }, 50);
  }, [token]);

  const handleResizeMove = useCallback((e) => {
    if (!resizingApp) return;
    
    const deltaY = e.clientY - dragStartY;
    const deltaSlots = Math.round(deltaY / SLOT_HEIGHT);
    const newDurata = Math.max(15, originalDurata + (deltaSlots * SLOT_MINUTES));
    
    setAppuntamenti(prev => prev.map(a => 
      a.id === resizingApp.id 
        ? { ...a, durata_totale: newDurata, ora_fine: minutiToOra(oraToMinuti(a.ora) + newDurata) }
        : a
    ));
  }, [resizingApp, dragStartY, originalDurata]);

  const handleResizeEnd = useCallback(async () => {
    if (resizingApp) {
      const app = appuntamenti.find(a => a.id === resizingApp.id);
      if (app && app.durata_totale !== originalDurata) {
        try {
          await axios.put(`${API}/appuntamenti/${app.id}/durata`, 
            { durata_totale: app.durata_totale },
            { headers: { Authorization: `Bearer ${token}` } }
          );
          toast.success(`Durata aggiornata: ${app.durata_totale} min`);
        } catch (error) {
          setAppuntamenti(prev => prev.map(a => 
            a.id === resizingApp.id ? { ...a, durata_totale: originalDurata } : a
          ));
          toast.error("Errore nell'aggiornamento");
        }
      }
    }
    
    setResizingApp(null);
    document.removeEventListener('mousemove', handleResizeMove);
    document.removeEventListener('mouseup', handleResizeEnd);
  }, [resizingApp, appuntamenti, originalDurata, token]);

  const handleDoubleClick = (e, app) => {
    e.stopPropagation();
    // Doppio click: apri il Quick Edit Dialog (vecchio flusso completo)
    setQuickEditApp(app);
    setQuickEditOpen(true);
  };

  // Vai al profilo cliente dall'appuntamento corrente
  const goToProfiloCliente = () => {
    if (!quickEditApp?.cliente_id) {
      toast.error("Nessun cliente collegato a questo appuntamento");
      return;
    }
    const returnState = {
      fromAgenda: true,
      data: format(dataCorrente, 'yyyy-MM-dd'),
      appuntamentoId: quickEditApp.id
    };
    setQuickEditOpen(false);
    setTimeout(() => {
      navigate(`/clienti?clienteId=${quickEditApp.cliente_id}`, { state: returnState });
    }, 100);
  };

  // Click diretto sul NOME del cliente in un blocco appuntamento dell'agenda
  // -> apre il profilo cliente preservando il contesto (data/operatore per il "Torna all'Agenda").
  const handleClienteClickFromCard = (app) => {
    if (!app?.cliente_id) {
      toast.error("Nessun cliente collegato a questo appuntamento");
      return;
    }
    const returnState = {
      fromAgenda: true,
      data: format(dataCorrente, 'yyyy-MM-dd'),
      appuntamentoId: app.id
    };
    navigate(`/clienti?clienteId=${app.cliente_id}`, { state: returnState });
  };

  // Vai alla modifica dati anagrafici cliente (nome, cognome, telefono, data nascita...)
  const goToModificaDatiCliente = () => {
    if (!quickEditApp?.cliente_id) {
      toast.error("Nessun cliente collegato a questo appuntamento");
      return;
    }
    const returnState = {
      fromAgenda: true,
      data: format(dataCorrente, 'yyyy-MM-dd'),
      appuntamentoId: quickEditApp.id
    };
    setQuickEditOpen(false);
    setTimeout(() => {
      navigate(`/clienti?clienteId=${quickEditApp.cliente_id}&edit=true`, { state: returnState });
    }, 100);
  };

  // Sposta l'ora dell'appuntamento di ±15 min (mantiene il giorno)
  const adjustOra = async (minutes) => {
    if (!quickEditApp) return;
    const totMin = oraToMinuti(quickEditApp.ora) + minutes;
    if (totMin < 0 || totMin >= 24 * 60) {
      toast.error("Orario non valido");
      return;
    }
    const nuovaOra = minutiToOra(totMin);
    const nuovaOraFine = minutiToOra(totMin + (quickEditApp.durata_totale || 30));
    const originalOra = quickEditApp.ora;
    const originalOraFine = quickEditApp.ora_fine;

    // Optimistic update
    setQuickEditApp(prev => ({ ...prev, ora: nuovaOra, ora_fine: nuovaOraFine }));
    setAppuntamenti(prev => prev.map(a =>
      a.id === quickEditApp.id ? { ...a, ora: nuovaOra, ora_fine: nuovaOraFine } : a
    ));

    try {
      await axios.put(`${API}/appuntamenti/${quickEditApp.id}`, {
        ...quickEditApp,
        ora: nuovaOra,
        ora_fine: nuovaOraFine
      }, { headers: { Authorization: `Bearer ${token}` } });
      toast.success(`Ora: ${nuovaOra}`);
    } catch (error) {
      // Rollback
      setQuickEditApp(prev => ({ ...prev, ora: originalOra, ora_fine: originalOraFine }));
      setAppuntamenti(prev => prev.map(a =>
        a.id === quickEditApp.id ? { ...a, ora: originalOra, ora_fine: originalOraFine } : a
      ));
      toast.error("Errore nello spostamento orario");
    }
  };

  // Funzione alternativa per quick edit (click singolo lungo o altro)
  const handleQuickEdit = (e, app) => {
    e.stopPropagation();
    setQuickEditApp(app);
    setQuickEditOpen(true);
  };

  // Funzione per scrollare all'appuntamento selezionato
  const scrollToAppuntamento = (appId) => {
    if (!appId) return;
    setTimeout(() => {
      const element = document.querySelector(`[data-appuntamento-id="${appId}"]`);
      if (element) {
        element.scrollIntoView({ behavior: 'smooth', block: 'center' });
        // Flash effect
        element.classList.add('ring-2', 'ring-purple-500', 'ring-offset-2');
        setTimeout(() => {
          element.classList.remove('ring-2', 'ring-purple-500', 'ring-offset-2');
        }, 2000);
      }
    }, 100);
  };

  // Chiudi dialog e scrolla
  const closeQuickEditAndScroll = () => {
    const appId = quickEditApp?.id;
    setQuickEditOpen(false);
    scrollToAppuntamento(appId);
  };

  const adjustDuration = async (minutes) => {
    if (!quickEditApp) return;
    
    const newDurata = Math.max(15, (quickEditApp.durata_totale || 30) + minutes);
    const newOraFine = minutiToOra(oraToMinuti(quickEditApp.ora) + newDurata);
    
    try {
      await axios.put(`${API}/appuntamenti/${quickEditApp.id}/durata`, 
        { durata_totale: newDurata },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      // Aggiorna prima il quickEditApp (che è visibile nel dialog)
      setQuickEditApp(prev => ({ ...prev, durata_totale: newDurata, ora_fine: newOraFine }));
      
      // Poi aggiorna la lista con un piccolo delay per evitare conflitti DOM
      requestAnimationFrame(() => {
        setAppuntamenti(prev => prev.map(a => 
          a.id === quickEditApp.id ? { ...a, durata_totale: newDurata, ora_fine: newOraFine } : a
        ));
      });
      
      toast.success(`Durata: ${newDurata} min`);
    } catch (error) {
      toast.error("Errore nell'aggiornamento");
    }
  };

  const deleteAppointment = async (eliminaFuturi = false) => {
    if (!quickEditApp) return;
    
    try {
      // Se è ricorrente e non abbiamo chiesto, mostra il dialog
      if (quickEditApp.ricorrente && quickEditApp.ricorrenza_id && !deleteRicorrenteOpen) {
        setDeleteRicorrenteOpen(true);
        return;
      }
      
      // Procedi con l'eliminazione
      const params = quickEditApp.ricorrente && eliminaFuturi 
        ? `?elimina_futuri=true` 
        : '';
      
      const appIdToDelete = quickEditApp.id;
      const ricorrenzaId = quickEditApp.ricorrenza_id;
      const dataApp = quickEditApp.data;
      
      const response = await axios.delete(`${API}/appuntamenti/${appIdToDelete}${params}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      // Chiudi prima il dialog
      setQuickEditOpen(false);
      setDeleteRicorrenteOpen(false);
      setQuickEditApp(null);
      
      // Poi aggiorna la lista con delay per evitare conflitti DOM
      setTimeout(() => {
        if (eliminaFuturi && response.data.deleted_count > 1) {
          setAppuntamenti(prev => prev.filter(a => 
            a.ricorrenza_id !== ricorrenzaId || 
            a.data < dataApp
          ));
          toast.success(`Eliminati ${response.data.deleted_count} appuntamenti futuri`);
        } else {
          setAppuntamenti(prev => prev.filter(a => a.id !== appIdToDelete));
          toast.success("Appuntamento eliminato");
        }
      }, 100);
    } catch (error) {
      toast.error("Errore nell'eliminazione");
    }
  };

  const goToCassa = () => {
    if (!quickEditApp) return;
    const appuntamento = { ...quickEditApp }; // Copia per evitare stale reference
    setQuickEditOpen(false);
    // Naviga con delay per permettere al dialog di chiudersi
    setTimeout(() => {
      navigate('/cassa', { state: { appuntamento, fromAgenda: true } });
    }, 100);
  };

  // ==================== RICERCA CLIENTE RAPIDA ====================
  
  // Ref per debounce
  const searchTimeoutRef = useRef(null);
  
  // Cerca clienti con debounce
  const handleClienteSearch = (query) => {
    setClienteSearchQuery(query);
    
    // Cancella timeout precedente
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }
    
    if (query.length < 1) {
      setClienteSearchResults([]);
      return;
    }
    
    // Debounce 200ms - veloce ma non troppo
    searchTimeoutRef.current = setTimeout(async () => {
      try {
        const response = await axios.get(`${API}/clienti?search=${encodeURIComponent(query)}&limit=50`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setClienteSearchResults(response.data || []);
      } catch (error) {
        console.error("Errore ricerca cliente:", error);
        setClienteSearchResults([]);
      }
    }, 200);
  };
  
  // Carica appuntamenti del cliente selezionato (versione veloce)
  const loadClienteAppuntamenti = async (cliente) => {
    setClienteSelezionato(cliente);
    setLoadingClienteApp(true);
    setClienteSearchResults([]);
    setClienteSearchQuery(`${cliente.nome} ${cliente.cognome || ''}`);
    
    try {
      const response = await axios.get(`${API}/clienti/${cliente.id}/appuntamenti-tutti?limit_futuri=10&limit_passati=5`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setClienteAppuntamenti({
        futuri: response.data.futuri || [],
        passati: response.data.passati || [],
        ricorrenti: response.data.ricorrenti || []
      });
    } catch (error) {
      setClienteAppuntamenti({ futuri: [], passati: [], ricorrenti: [] });
    } finally {
      setLoadingClienteApp(false);
    }
  };
  
  // Vai a un appuntamento specifico
  const goToAppuntamento = (app) => {
    setDataCorrente(new Date(app.data + 'T00:00:00'));
    setClienteSearchOpen(false);
    setClienteSelezionato(null);
    setClienteSearchQuery("");
    setClienteAppuntamenti({ futuri: [], passati: [], ricorrenti: [] });
  };
  
  // Chiudi ricerca
  const closeClienteSearch = () => {
    setClienteSearchOpen(false);
    setClienteSelezionato(null);
    setClienteSearchQuery("");
    setClienteSearchResults([]);
    setClienteAppuntamenti({ futuri: [], passati: [], ricorrenti: [] });
  };
  
  // ==================== FINE RICERCA CLIENTE ====================
  
  // Aggiungi servizio all'appuntamento esistente
  const aggiungiServizioAppuntamento = async (servizio) => {
    if (!quickEditApp) return;
    
    try {
      // Aggiungi e auto-ordina secondo l'ordine standard di esecuzione del salone
      const nuoviServizi = ordinaServiziSalone([...(quickEditApp.servizi || []), servizio]);
      const nuovaDurata = nuoviServizi.reduce((sum, s) => sum + (s.durata_minuti || 30), 0);
      const nuovoTotale = nuoviServizi.reduce((sum, s) => sum + (s.prezzo || 0), 0);
      const nuovaOraFine = minutiToOra(oraToMinuti(quickEditApp.ora) + nuovaDurata);
      
      await axios.put(`${API}/appuntamenti/${quickEditApp.id}`, {
        ...quickEditApp,
        servizi: nuoviServizi,
        durata_totale: nuovaDurata,
        totale: nuovoTotale,
        ora_fine: nuovaOraFine
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      // Aggiorna stato locale
      const updatedApp = {
        ...quickEditApp,
        servizi: nuoviServizi,
        durata_totale: nuovaDurata,
        totale: nuovoTotale,
        ora_fine: nuovaOraFine
      };
      
      setAppuntamenti(prev => prev.map(a => 
        a.id === quickEditApp.id ? updatedApp : a
      ));
      setQuickEditApp(updatedApp);
      setAddServizioOpen(false);
      setSearchServizioAgenda("");
      
      toast.success(`${servizio.nome} aggiunto (€${servizio.prezzo})`);
    } catch (error) {
      toast.error("Errore nell'aggiunta del servizio");
    }
  };
  
  // Rimuovi servizio dall'appuntamento
  const rimuoviServizioAppuntamento = async (servizioIdx) => {
    if (!quickEditApp || quickEditApp.servizi.length <= 1) {
      toast.error("Deve esserci almeno un servizio");
      return;
    }
    
    try {
      const nuoviServizi = quickEditApp.servizi.filter((_, idx) => idx !== servizioIdx);
      const nuovaDurata = nuoviServizi.reduce((sum, s) => sum + (s.durata_minuti || 30), 0);
      const nuovoTotale = nuoviServizi.reduce((sum, s) => sum + (s.prezzo || 0), 0);
      const nuovaOraFine = minutiToOra(oraToMinuti(quickEditApp.ora) + nuovaDurata);
      
      await axios.put(`${API}/appuntamenti/${quickEditApp.id}`, {
        ...quickEditApp,
        servizi: nuoviServizi,
        durata_totale: nuovaDurata,
        totale: nuovoTotale,
        ora_fine: nuovaOraFine
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      const updatedApp = {
        ...quickEditApp,
        servizi: nuoviServizi,
        durata_totale: nuovaDurata,
        totale: nuovoTotale,
        ora_fine: nuovaOraFine
      };
      
      setAppuntamenti(prev => prev.map(a => 
        a.id === quickEditApp.id ? updatedApp : a
      ));
      setQuickEditApp(updatedApp);
      
      toast.success("Servizio rimosso");
    } catch (error) {
      toast.error("Errore nella rimozione del servizio");
    }
  };
  
  // Filtra servizi per ricerca
  const serviziFiltratAgenda = servizi.filter(s => 
    s.nome.toLowerCase().includes(searchServizioAgenda.toLowerCase())
  );

  // Invia promemoria WhatsApp/Email
  const inviaPromemoria = async () => {
    if (!quickEditApp) return;
    
    setInvioPromemoriaLoading(true);
    try {
      const response = await axios.post(`${API}/notifiche/invia-promemoria`, {
        appuntamento_id: quickEditApp.id,
        canali: ["whatsapp", "email"]
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      const result = response.data;
      
      // Mostra risultato
      let messaggi = [];
      if (result.whatsapp?.success) {
        messaggi.push("WhatsApp inviato ✓");
      } else if (result.whatsapp?.error) {
        messaggi.push(`WhatsApp: ${result.whatsapp.error}`);
      }
      
      if (result.email?.success) {
        messaggi.push("Email inviata ✓");
      } else if (result.email?.error) {
        messaggi.push(`Email: ${result.email.error}`);
      }
      
      if (messaggi.length > 0) {
        if (result.whatsapp?.success || result.email?.success) {
          toast.success(`Promemoria inviato: ${messaggi.join(', ')}`);
        } else {
          toast.error(`Errore invio: ${messaggi.join(', ')}`);
        }
      } else {
        toast.warning("Nessun contatto disponibile per l'invio");
      }
      
      // Aggiorna stato appuntamento
      setAppuntamenti(prev => prev.map(a => 
        a.id === quickEditApp.id 
          ? { ...a, promemoria_inviato: true } 
          : a
      ));
      
    } catch (error) {
      toast.error("Errore nell'invio del promemoria");
    } finally {
      setInvioPromemoriaLoading(false);
    }
  };

  // Apre dialog consulenza per un cliente specifico
  const openConsulenzaDialog = (clienteId, clienteNome, e) => {
    if (e) {
      e.stopPropagation();
      e.preventDefault();
    }
    setConsulenzaCliente({ id: clienteId, nome: clienteNome });
    setConsulenzaFile(null);
    setConsulenzaDescrizione("");
    setConsulenzaDialogOpen(true);
  };

  // Gestisce selezione file consulenza
  const handleConsulenzaFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (file.type !== 'application/pdf') {
        toast.error("Seleziona un file PDF");
        return;
      }
      if (file.size > 10 * 1024 * 1024) {
        toast.error("Il file deve essere inferiore a 10MB");
        return;
      }
      setConsulenzaFile(file);
    }
  };

  // Upload consulenza
  const handleUploadConsulenza = async () => {
    if (!consulenzaFile) {
      toast.error("Seleziona un file PDF");
      return;
    }
    if (!consulenzaCliente) {
      toast.error("Nessun cliente selezionato");
      return;
    }

    setConsulenzaUploading(true);
    
    try {
      const formData = new FormData();
      formData.append('file', consulenzaFile);
      formData.append('cliente_id', consulenzaCliente.id);
      formData.append('cliente_nome', consulenzaCliente.nome);
      formData.append('descrizione', consulenzaDescrizione);

      await axios.post(`${API}/consulenze/upload-pdf`, formData, {
        headers: { 
          Authorization: `Bearer ${token}`,
          'Content-Type': 'multipart/form-data'
        }
      });

      toast.success("Consulenza caricata con successo!");
      
      // Aggiorna lo stato consulenze
      setConsulenzeStatus(prev => ({
        ...prev,
        [consulenzaCliente.id]: {
          has_consulenza: true,
          count: 1,
          last_date: new Date().toISOString()
        }
      }));
      
      setConsulenzaDialogOpen(false);
      setConsulenzaFile(null);
      setConsulenzaCliente(null);
      setConsulenzaDescrizione("");
    } catch (error) {
      toast.error("Errore nel caricamento del file");
    } finally {
      setConsulenzaUploading(false);
    }
  };

  // Crea nuovo cliente veloce
  const handleCreaNuovoCliente = async () => {
    if (!nuovoCliente.nome?.trim() || !nuovoCliente.cognome?.trim()) {
      toast.error("Nome e Cognome sono obbligatori");
      return;
    }
    if (!nuovoCliente.telefono?.trim()) {
      toast.error("Il numero di telefono è obbligatorio");
      return;
    }
    if (!nuovoCliente.genere || !["M", "F"].includes(nuovoCliente.genere.toUpperCase())) {
      toast.error("Seleziona il sesso (M / F)");
      return;
    }
    
    try {
      const response = await axios.post(`${API}/clienti`, {
        nome: nuovoCliente.nome.trim(),
        cognome: nuovoCliente.cognome.trim(),
        telefono: nuovoCliente.telefono.trim(),
        genere: nuovoCliente.genere.toUpperCase(),
        email: '',
        data_nascita: '',
        note: ''
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      // Aggiungi alla lista clienti locale
      const newCliente = response.data;
      setClienti(prev => [...prev, newCliente]);
      
      // Seleziona automaticamente il nuovo cliente (usa callback per evitare stale closure)
      setFormData(prev => ({
        ...prev,
        cliente_id: newCliente.id,
        cliente_nome: `${newCliente.nome} ${newCliente.cognome}`.trim(),
        cliente_nuovo: true // Marca come cliente nuovo
      }));
      
      // Reset e chiudi
      setNuovoCliente({ nome: '', cognome: '', telefono: '', genere: '' });
      setNuovoClienteOpen(false);
      toast.success("Cliente creato e selezionato!");
    } catch (error) {
      console.error("Errore creazione cliente:", error);
      toast.error("Errore nella creazione del cliente");
    }
  };

  const openNewAppointment = (operatoreId, ora) => {
    const operatore = operatori.find(o => o.id === operatoreId);
    setSelectedSlot({ operatoreId, ora });
    setFormData({
      cliente_id: "",
      cliente_nome: "",
      data: format(dataCorrente, 'yyyy-MM-dd'),
      ora: ora,
      servizi: [],
      note: "",
      operatore_id: operatoreId,
      operatore_nome: operatore ? `${operatore.nome} ${operatore.cognome || ''}`.trim() : ""
    });
    setDialogOpen(true);
  };

  const handleClienteChange = (clienteId) => {
    const cliente = clienti.find(c => c.id === clienteId);
    setFormData({
      ...formData,
      cliente_id: clienteId,
      cliente_nome: cliente ? `${cliente.nome} ${cliente.cognome}` : ""
    });
    setSearchCliente(""); // Chiude la lista dropdown
  };

  const handleServizioToggle = (servizio) => {
    const servizioData = {
      nome: servizio.nome,
      prezzo: servizio.prezzo,
      durata_minuti: servizio.durata_minuti,
      categoria: servizio.categoria,
      tipo: servizio.tipo,
      ordine_schema: servizio.ordine_schema || 0,
      tempo_lavoro: servizio.tempo_lavoro || 0,
      tempo_posa: servizio.tempo_posa || 0,
      steps: Array.isArray(servizio.steps) ? servizio.steps : [],
      colore: servizio.colore || "#3B82F6"
    };
    
    const exists = formData.servizi.find(s => s.nome === servizio.nome);
    if (exists) {
      // Rimuovi il servizio e i suoi associati
      const associatiIds = servizio.servizi_associati || [];
      const associatiNomi = servizi
        .filter(s => associatiIds.includes(s.id))
        .map(s => s.nome);
      
      setFormData({ 
        ...formData, 
        servizi: formData.servizi.filter(s => s.nome !== servizio.nome && !associatiNomi.includes(s.nome))
      });
    } else {
      // Aggiungi il servizio principale
      let nuoviServizi = [...formData.servizi, servizioData];
      
      // Aggiungi automaticamente i servizi associati
      const associatiIds = servizio.servizi_associati || [];
      if (associatiIds.length > 0) {
        associatiIds.forEach(associatoId => {
          const servizioAssociato = servizi.find(s => s.id === associatoId);
          if (servizioAssociato && !nuoviServizi.find(s => s.nome === servizioAssociato.nome)) {
            nuoviServizi.push({
              nome: servizioAssociato.nome,
              prezzo: servizioAssociato.prezzo,
              durata_minuti: servizioAssociato.durata_minuti,
              categoria: servizioAssociato.categoria,
              tipo: servizioAssociato.tipo,
              ordine_schema: servizioAssociato.ordine_schema || 0,
              tempo_lavoro: servizioAssociato.tempo_lavoro || 0,
              tempo_posa: servizioAssociato.tempo_posa || 0,
              steps: Array.isArray(servizioAssociato.steps) ? servizioAssociato.steps : [],
              colore: servizioAssociato.colore || "#3B82F6",
              is_associato: true // Flag per indicare che è stato aggiunto automaticamente
            });
          }
        });
        
        // Notifica l'utente
        const nomiAssociati = servizi
          .filter(s => associatiIds.includes(s.id))
          .map(s => s.nome)
          .join(", ");
        if (nomiAssociati) {
          toast.info(`Aggiunto automaticamente: ${nomiAssociati}`);
        }
      }
      
      // Auto-ordinamento secondo l'ordine standard di esecuzione del salone
      nuoviServizi = ordinaServiziSalone(nuoviServizi);
      setFormData({ ...formData, servizi: nuoviServizi });
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validazione
    if (!formData.cliente_id && !formData.cliente_nome) {
      toast.error("Seleziona un cliente");
      return;
    }
    if (!formData.ora) {
      toast.error("Seleziona un orario");
      return;
    }
    if (!formData.operatore_id) {
      toast.error("Seleziona un operatore");
      return;
    }
    if (formData.servizi.length === 0) {
      toast.error("Seleziona almeno un servizio");
      return;
    }
    
    try {
      const dataToSend = {
        ...formData,
        data: formData.data || format(dataCorrente, 'yyyy-MM-dd'),
        is_slot_posa: formData.is_slot_posa || false
      };
      const response = await axios.post(`${API}/appuntamenti`, dataToSend, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      // Mostra messaggio appropriato PRIMA di chiudere
      if (response.data.count && response.data.count > 1) {
        toast.success(`Creati ${response.data.count} appuntamenti ricorrenti! 🔄`);
      } else {
        toast.success("Appuntamento creato!");
      }
      
      // Usa requestAnimationFrame per aspettare il prossimo frame
      // poi chiudi il dialog per evitare conflitti DOM
      requestAnimationFrame(() => {
        setDialogOpen(false);
        setIsFromSlotPosa(false);
        resetForm();
        
        // Aggiorna lista con delay aggiuntivo per sicurezza
        setTimeout(async () => {
          try {
            const dataStr = format(dataCorrente, 'yyyy-MM-dd');
            const [appRes, remindersRes] = await Promise.all([
              axios.get(`${API}/appuntamenti?data=${dataStr}`, {
                headers: { Authorization: `Bearer ${token}` }
              }),
              axios.get(`${API}/marketing/reminders?data=${dataStr}`, {
                headers: { Authorization: `Bearer ${token}` }
              }).catch(() => ({ data: [] }))
            ]);
            setAppuntamenti(appRes.data || []);
            setMarketingReminders(remindersRes.data || []);
          } catch {
            // Silently fail - l'appuntamento è stato creato
          }
        }, 200);
      });
    } catch (error) {
      toast.error(error.response?.data?.detail || "Errore nella creazione");
    }
  };

  // Blocco operatore handlers
  const openBloccoDialog = (operatoreId, tipo) => {
    const tipoBlocco = TIPI_BLOCCO.find(t => t.id === tipo);
    const defaultOraInizio = tipo === 'pausa_pranzo' ? '13:00' : '09:00';
    const defaultOraFine = tipo === 'pausa_pranzo' ? '14:00' : 
                          tipo === 'ferie' ? '19:00' : '10:00';
    
    setBloccoData({
      operatore_id: operatoreId,
      tipo: tipo,
      ora_inizio: defaultOraInizio,
      ora_fine: defaultOraFine,
      note: ""
    });
    setBloccoDialogOpen(true);
  };

  const handleBloccoSubmit = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API}/blocchi-operatore`, {
        ...bloccoData,
        data: format(dataCorrente, 'yyyy-MM-dd')
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Blocco aggiunto!");
      setBloccoDialogOpen(false);
      fetchBlocchiOperatore();
    } catch (error) {
      toast.error("Errore nell'aggiunta del blocco");
    }
  };

  const deleteBlocco = async (bloccoId) => {
    try {
      await axios.delete(`${API}/blocchi-operatore/${bloccoId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Blocco rimosso");
      fetchBlocchiOperatore();
    } catch (error) {
      toast.error("Errore nella rimozione");
    }
  };

  const isIncassato = (app) => {
    return app.stato === 'completato' || app.stato === 'pagato' || app.pagato;
  };

  const totaleServizi = formData.servizi.reduce((sum, s) => sum + s.prezzo, 0);
  const durataTotale = formData.servizi.reduce((sum, s) => sum + s.durata_minuti, 0);
  const isToday = isSameDay(dataCorrente, new Date());
  // operatoriAttivi è già definito con useMemo sopra

  // Ottieni il genere del cliente selezionato nel form
  const clienteFormSelezionato = clienti.find(c => c.id === formData.cliente_id);
  const genereCliente = clienteFormSelezionato?.genere || '';
  
  // Determina la categoria servizi in base al genere del cliente
  // Se cliente uomo (M) → solo servizi uomo + unisex
  // Se cliente donna (F) → solo servizi donna + unisex
  // Se non specificato → usa filterCategoria manuale
  const categoriaServiziPerCliente = genereCliente === 'M' ? 'uomo' : 
                                      genereCliente === 'F' ? 'donna' : 
                                      filterCategoria;

  // Filtra servizi: mostra solo categoria appropriata + unisex
  const filteredServizi = servizi.filter(s => 
    s.categoria === categoriaServiziPerCliente || s.categoria === "unisex"
  );

  const calcolaOraFine = () => {
    if (!formData.ora || durataTotale === 0) return "";
    const [h, m] = formData.ora.split(':').map(Number);
    const minutiTotali = h * 60 + m + durataTotale;
    const oreF = Math.floor(minutiTotali / 60);
    const minF = minutiTotali % 60;
    return `${oreF.toString().padStart(2, '0')}:${minF.toString().padStart(2, '0')}`;
  };

  return (
    <div className="h-[calc(100vh-120px)] sm:h-[calc(100vh-140px)] flex flex-col" data-testid="agenda-page">
      {/* Header Mobile Compatto */}
      <div className="flex flex-col gap-2 mb-2 sm:mb-4">
        {/* Riga 1: Navigazione data + Nuovo */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-0.5 sm:gap-1">
            <Button variant="ghost" size="sm" onClick={() => navigateDate(-1)} className="h-7 w-7 sm:h-8 sm:w-8 p-0" data-testid="btn-prev-day">
              <ChevronLeft className="w-4 h-4" />
            </Button>
            <Popover open={calendarOpen} onOpenChange={setCalendarOpen}>
              <PopoverTrigger asChild>
                <Button variant="outline" size="sm" className="h-7 sm:h-8 px-2 text-xs sm:text-sm font-medium" data-testid="btn-calendar">
                  <CalendarDays className="w-3 h-3 sm:w-4 sm:h-4 mr-1 text-blue-600" />
                  <span className="hidden sm:inline">{format(dataCorrente, "d MMM yyyy", { locale: it })}</span>
                  <span className="sm:hidden">{format(dataCorrente, "d/M", { locale: it })}</span>
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-auto p-0" align="start">
                <CalendarComponent
                  mode="single"
                  selected={dataCorrente}
                  onSelect={(date) => { if (date) { setDataCorrente(date); setCalendarOpen(false); } }}
                  locale={it}
                  className="rounded-xl border-0"
                />
              </PopoverContent>
            </Popover>
            <Button variant="ghost" size="sm" onClick={() => navigateDate(1)} className="h-7 w-7 sm:h-8 sm:w-8 p-0" data-testid="btn-next-day">
              <ChevronRight className="w-4 h-4" />
            </Button>
            <Button variant="ghost" size="sm" onClick={goToToday} className={`h-7 sm:h-8 px-2 text-xs ${isToday ? 'bg-blue-50 text-blue-600' : ''}`} data-testid="btn-oggi">
              Oggi
            </Button>
          </div>
          <Button 
            onClick={() => { resetForm(); setIsEditing(false); setDialogOpen(true); }}
            size="sm"
            className="h-7 sm:h-8 px-2 sm:px-3 text-xs bg-blue-600 hover:bg-blue-700"
            data-testid="btn-nuovo-appuntamento"
          >
            <Plus className="w-3.5 h-3.5" />
            <span className="hidden sm:inline ml-1">Nuovo</span>
          </Button>
        </div>
        
        {/* Riga 2: Operatori + Vista */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-1">
            {isMobile && operatoriAttivi.length > 1 && (
              <div className="flex items-center bg-slate-100 rounded p-0.5 mr-1">
                <button onClick={() => { setNumOperatoriVisibili(1); setStartIndex(Math.min(startIndex, operatoriAttivi.length - 1)); }}
                  className={`px-1.5 py-0.5 text-[10px] font-medium rounded ${numOperatoriVisibili === 1 ? 'bg-blue-600 text-white' : 'text-slate-600'}`}>1</button>
                <button onClick={() => { setNumOperatoriVisibili(3); setStartIndex(Math.min(startIndex, Math.max(0, operatoriAttivi.length - 3))); }}
                  className={`px-1.5 py-0.5 text-[10px] font-medium rounded ${numOperatoriVisibili === 3 ? 'bg-blue-600 text-white' : 'text-slate-600'}`}>3</button>
              </div>
            )}
            {operatoriAttivi.length > numOperatoriVisibili && (
              <div className="flex items-center gap-0.5">
                <Button variant="ghost" size="sm" onClick={() => navigateOperatori(-1)} disabled={startIndex === 0} className="h-6 w-6 p-0">
                  <ChevronLeft className="w-3 h-3" />
                </Button>
                <span className="text-[10px] text-slate-500 min-w-[28px] text-center">{startIndex + 1}-{Math.min(startIndex + numOperatoriVisibili, operatoriAttivi.length)}</span>
                <Button variant="ghost" size="sm" onClick={() => navigateOperatori(1)} disabled={startIndex >= operatoriAttivi.length - numOperatoriVisibili} className="h-6 w-6 p-0">
                  <ChevronRight className="w-3 h-3" />
                </Button>
              </div>
            )}
          </div>
          <div className="flex items-center gap-1">
            {/* Toggle Giorno / Settimana */}
            <div className="flex bg-slate-100 rounded p-0.5 mr-1">
              <button
                onClick={() => setModalitaVista('giorno')}
                className={`px-2 py-0.5 rounded text-[10px] font-semibold ${modalitaVista === 'giorno' ? 'bg-white shadow-sm text-slate-800' : 'text-slate-500'}`}
                data-testid="btn-vista-giorno"
              >
                Giorno
              </button>
              <button
                onClick={() => setModalitaVista('settimana')}
                className={`px-2 py-0.5 rounded text-[10px] font-semibold ${modalitaVista === 'settimana' ? 'bg-white shadow-sm text-slate-800' : 'text-slate-500'}`}
                data-testid="btn-vista-settimana"
              >
                Settimana
              </button>
            </div>
            <div className="flex bg-slate-100 rounded p-0.5">
              <button onClick={() => setVistaCompatta(false)} className={`p-1 rounded ${!vistaCompatta ? 'bg-white shadow-sm' : ''}`}>
                <LayoutGrid className="w-3 h-3 sm:w-3.5 sm:h-3.5 text-slate-600" />
              </button>
              <button onClick={() => setVistaCompatta(true)} className={`p-1 rounded ${vistaCompatta ? 'bg-white shadow-sm' : ''}`}>
                <List className="w-3 h-3 sm:w-3.5 sm:h-3.5 text-slate-600" />
              </button>
            </div>
            <Button variant="ghost" size="sm" onClick={() => setClienteSearchOpen(true)} className="h-6 w-6 sm:h-7 sm:w-7 p-0" title="Cerca cliente">
              <Search className="w-3 h-3 sm:w-3.5 sm:h-3.5" />
            </Button>
          </div>
        </div>
      </div>

      {/* Calendar Grid per Operatori */}
      <Card className="flex-1 border-0 shadow-lg overflow-hidden relative" style={{ maxWidth: '100vw' }}>
        {/* ═══════════ VISTA SETTIMANALE (Lun-Sab) ═══════════ */}
        {modalitaVista === 'settimana' && (
          <div className="p-2">
            <AgendaSettimanaleView
              dataCorrente={dataCorrente}
              appuntamenti={appuntamenti}
              operatori={operatori}
              servizi={servizi}
              oraApertura={parseInt((orariGiorno.apertura || '09:00').split(':')[0], 10)}
              oraChiusura={parseInt((orariGiorno.chiusura || '19:00').split(':')[0], 10)}
              onSelectDay={(g) => { setDataCorrente(g); setModalitaVista('giorno'); }}
              onSelectApp={(app) => {
                // Naviga al giorno dell'appuntamento e apri quick edit
                const [y, m, d] = (app.data || '').split('-').map(Number);
                if (y && m && d) setDataCorrente(new Date(y, m - 1, d));
                setModalitaVista('giorno');
              }}
            />
          </div>
        )}

        {/* ═══════════ VISTA GIORNALIERA (default) ═══════════ */}
        {modalitaVista === 'giorno' && <>{/* Banner Marketing Reminders */}
        {marketingReminders.length > 0 && !saloneChiuso && (
          <div className="bg-gradient-to-r from-purple-500 to-purple-600 text-white px-4 py-2" data-testid="marketing-reminders-banner">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Bell className="w-4 h-4 animate-pulse" />
                <span className="font-medium text-sm" data-testid="reminders-count">
                  {marketingReminders.length} cliente{marketingReminders.length > 1 ? 'i' : ''} da richiamare oggi
                </span>
              </div>
              <div className="flex items-center gap-2 overflow-x-auto" data-testid="reminders-list">
                {marketingReminders.slice(0, 3).map((r) => (
                  <button
                    key={r.id}
                    onClick={() => {
                      // Apri telefono o mostra dettagli
                      if (r.cliente_telefono) {
                        window.open(`tel:${r.cliente_telefono}`);
                      }
                    }}
                    className="bg-white/20 hover:bg-white/30 px-2 py-1 rounded text-xs flex items-center gap-1 whitespace-nowrap transition-colors"
                    data-testid={`reminder-button-${r.id}`}
                  >
                    <Phone className="w-3 h-3" />
                    {r.cliente_nome}
                    {r.servizio_suggerito && (
                      <span className="opacity-70">({r.servizio_suggerito})</span>
                    )}
                  </button>
                ))}
                {marketingReminders.length > 3 && (
                  <span className="text-xs opacity-70">+{marketingReminders.length - 3} altri</span>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Overlay salone chiuso */}
        {saloneChiuso && (
          <div className="absolute inset-0 bg-slate-800/90 z-30 flex items-center justify-center">
            <div className="text-center p-8 bg-white rounded-2xl shadow-xl max-w-md mx-4">
              <div className="w-16 h-16 mx-auto mb-4 bg-red-100 rounded-full flex items-center justify-center">
                {orariGiorno.motivo_chiusura ? (
                  <Palmtree className="w-8 h-8 text-red-500" />
                ) : (
                  <X className="w-8 h-8 text-red-500" />
                )}
              </div>
              <h3 className="text-xl font-bold text-slate-800 mb-2">
                {orariGiorno.motivo_chiusura || "Salone Chiuso"}
              </h3>
              <p className="text-slate-600 mb-4">
                {format(dataCorrente, "EEEE d MMMM", { locale: it })} 
                {orariGiorno.motivo_chiusura 
                  ? ` - ${orariGiorno.motivo_chiusura}` 
                  : " è un giorno di chiusura."}
              </p>
              <p className="text-sm text-slate-500">
                Seleziona un altro giorno per visualizzare o creare appuntamenti.
              </p>
              <Button 
                onClick={() => navigateDate(1)} 
                className="mt-4 bg-blue-600 hover:bg-blue-700"
              >
                <ChevronRight className="w-4 h-4 mr-2" />
                Vai al giorno successivo
              </Button>
            </div>
          </div>
        )}
        
        <CardContent className="p-0 h-full">
          <div className="h-full overflow-y-auto overflow-x-hidden" ref={gridRef}>
            <div className="w-full">
              {/* Header operatori - ADAPTIVE */}
              <div 
                className="grid border-b border-slate-200 sticky top-0 bg-white/95 backdrop-blur-sm z-20 shadow-sm"
                style={{ gridTemplateColumns: `${vistaCompatta ? '32px' : '45px'} repeat(${operatoriVisibili.length}, 1fr)` }}
              >
                <div className="border-r border-slate-200 bg-slate-50/80"></div>
                {operatoriVisibili.map((operatore) => (
                  <div 
                    key={operatore.id}
                    data-testid={`operator-column-${operatore.id}`}
                    className="border-r border-slate-200 last:border-r-0"
                    style={{ backgroundColor: `${operatore.colore}15` }}
                  >
                    {/* Menu info operatore - MINI */}
                    <div className="flex items-center justify-between px-0.5 border-b" style={{ borderColor: `${operatore.colore}30` }}>
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button variant="ghost" size="sm" className="h-4 text-[8px] px-1 gap-0">
                            <Plus className="w-2 h-2" />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="start" className="min-w-[120px]">
                          {TIPI_BLOCCO.map((tipo) => (
                            <DropdownMenuItem 
                              key={tipo.id}
                              onClick={() => openBloccoDialog(operatore.id, tipo.id)}
                              className="gap-1 text-[10px] py-1"
                            >
                              <tipo.icon className="w-3 h-3" />
                              {tipo.label}
                            </DropdownMenuItem>
                          ))}
                        </DropdownMenuContent>
                      </DropdownMenu>
                      
                      {/* Badge blocchi attivi - MINI */}
                      <div className="flex gap-px">
                        {getBlocchiForOperatore(operatore.id).slice(0, 2).map(blocco => {
                          const tipo = TIPI_BLOCCO.find(t => t.id === blocco.tipo);
                          if (!tipo) return null;
                          return (
                            <div 
                              key={blocco.id}
                              className={`${tipo.color} text-white w-3 h-3 rounded-sm flex items-center justify-center cursor-pointer hover:opacity-80`}
                              title={`${tipo.label}: ${blocco.ora_inizio}-${blocco.ora_fine}`}
                              onClick={() => deleteBlocco(blocco.id)}
                            >
                              <tipo.icon className="w-2 h-2" />
                            </div>
                          );
                        })}
                      </div>
                    </div>
                    
                    {/* Nome operatore - ADAPTIVE */}
                    <div className={`text-center ${vistaCompatta ? 'py-1.5 px-1' : 'py-2 px-2'}`}>
                      <div 
                        className={`rounded-full mx-auto flex items-center justify-center text-white font-bold shadow-sm ${vistaCompatta ? 'w-6 h-6 md:w-7 md:h-7 text-[10px] md:text-xs mb-0.5' : 'w-8 h-8 md:w-10 md:h-10 text-xs md:text-sm mb-1'}`}
                        style={{ backgroundColor: operatore.colore }}
                      >
                        {operatore.nome.charAt(0)}{(operatore.cognome || '').charAt(0)}
                      </div>
                      <p className={`font-semibold text-slate-800 leading-tight truncate ${vistaCompatta ? 'text-[10px] md:text-xs' : 'text-xs md:text-sm'}`}>
                        {operatore.nome}
                      </p>
                      {!vistaCompatta && (
                        <p className="text-[10px] text-slate-500 leading-tight">{operatore.cognome}</p>
                      )}
                      
                      {/* Mostra orario turno operatore */}
                      <div className={vistaCompatta ? 'hidden md:block' : 'block'}>
                        {(() => {
                          const motivoFerie = isOperatoreInFerie(operatore, dataCorrente);
                          if (motivoFerie) {
                            return (
                              <div className="mt-0.5 px-1 py-0 bg-amber-100 rounded-full inline-block">
                                <span className={`text-amber-700 font-medium flex items-center gap-0.5 ${vistaCompatta ? 'text-[8px]' : 'text-[10px]'}`}>
                                  <Palmtree className={vistaCompatta ? 'w-2 h-2' : 'w-3 h-3'} />
                                  {vistaCompatta ? 'Ferie' : motivoFerie}
                                </span>
                              </div>
                            );
                          }
                          
                          const turno = getTurnoOperatore(operatore);
                          if (!turno) return null;
                          if (turno.attivo === false) {
                            return (
                              <div className="mt-0.5 px-1 py-0 bg-red-100 rounded-full inline-block">
                                <span className={`text-red-600 font-medium ${vistaCompatta ? 'text-[8px]' : 'text-[10px]'}`}>
                                  {vistaCompatta ? 'OFF' : 'RIPOSO'}
                                </span>
                              </div>
                            );
                          }
                          return (
                            <div className="mt-0.5 flex items-center justify-center gap-0.5">
                              <span className={`text-slate-500 ${vistaCompatta ? 'text-[8px]' : 'text-[10px]'}`}>
                                {turno.inizio || "09:00"}-{turno.fine || "19:00"}
                              </span>
                            </div>
                          );
                        })()}
                      </div>
                      
                      {/* Mansioni - solo vista espansa */}
                      {!vistaCompatta && operatore.mansioni && operatore.mansioni.length > 0 && (
                        <p className="text-[9px] text-slate-400 mt-0.5 truncate">
                          {operatore.mansioni.slice(0, 2).join(', ')}
                        </p>
                      )}
                    </div>
                  </div>
                ))}
              </div>

              {/* Time grid - ADAPTIVE */}
              <div 
                className="grid"
                style={{ gridTemplateColumns: `${vistaCompatta ? '32px' : '45px'} repeat(${operatoriVisibili.length}, 1fr)` }}
              >
                {/* Time labels - Mostra ogni 15 minuti */}
                <div className="border-r border-slate-200 bg-gradient-to-b from-slate-50 to-slate-100/30">
                  {ORARI_SALONE.filter(o => o.endsWith(':00') || o.endsWith(':30')).map(ora => {
                    const isHourMark = ora.endsWith(':00');
                    return (
                      <div 
                        key={ora}
                        className={`text-right pr-1 font-medium flex items-start justify-end ${
                          isHourMark 
                            ? `text-slate-500 ${vistaCompatta ? 'text-[9px] md:text-[10px]' : 'text-[10px] md:text-xs'}`
                            : `text-slate-300 ${vistaCompatta ? 'text-[7px] md:text-[8px]' : 'text-[8px] md:text-[9px]'}`
                        }`}
                        style={{ height: `${SLOT_HEIGHT * 6}px` }}
                      >
                        {isHourMark ? (vistaCompatta ? ora.slice(0, 2) : ora) : ora.slice(-2)}
                      </div>
                    );
                  })}
                </div>

                {/* Colonne operatori */}
                {operatoriVisibili.map((operatore) => {
                  const opApps = getAppuntamentiForOperatore(operatore.id);
                  const opBlocchi = getBlocchiForOperatore(operatore.id);
                  const isDragOver = dragOverOperatore === operatore.id;
                  const turnoOp = getTurnoOperatore(operatore);
                  const operatoreNonLavora = turnoOp && turnoOp.attivo === false;
                  const motivoFerieOp = isOperatoreInFerie(operatore, dataCorrente);
                  const operatoreInFerie = motivoFerieOp !== null;
                  const operatoreNonDisponibile = operatoreNonLavora || operatoreInFerie;
                  
                  return (
                    <div 
                      key={operatore.id}
                      data-testid={`operator-grid-${operatore.id}`}
                      className={`relative border-r border-slate-100 last:border-r-0 transition-colors ${
                        isDragOver ? 'bg-blue-50' : ''
                      } ${operatoreNonDisponibile ? 'opacity-50' : ''}`}
                      style={{ 
                        height: `${ORARI_SALONE.length * SLOT_HEIGHT}px`,
                        backgroundColor: isDragOver ? undefined : operatoreNonDisponibile ? '#f1f5f9' : `${operatore.colore}05`
                      }}
                      onDragOver={(e) => handleDragOver(e, operatore.id)}
                      onDragLeave={handleDragLeave}
                      onDrop={(e) => handleDrop(e, operatore.id)}
                    >
                      {/* Overlay operatore non lavora o in ferie */}
                      {operatoreNonDisponibile && (
                        <div className="absolute inset-0 bg-slate-200/80 z-5 flex items-center justify-center">
                          <div className="text-center p-4">
                            {operatoreInFerie ? (
                              <>
                                <Palmtree className="w-8 h-8 mx-auto text-amber-500 mb-2" />
                                <p className="text-sm font-medium text-amber-700">{motivoFerieOp}</p>
                              </>
                            ) : (
                              <>
                                <UserX className="w-8 h-8 mx-auto text-slate-400 mb-2" />
                                <p className="text-sm font-medium text-slate-500">Non lavora oggi</p>
                              </>
                            )}
                          </div>
                        </div>
                      )}
                      
                      {/* Grid lines - MODERN THIN */}
                      {ORARI_SALONE.map((ora, idx) => {
                        // RIMOSSA pausa salone - ogni operatore ha la sua pausa individuale
                        const isOperatoreOff = !isOperatoreDisponibile(operatore, ora);
                        const isBlocked = isOperatoreOff;
                        const isHourMark = ora.endsWith(':00');
                        const isQuarterMark = ora.endsWith(':15') || ora.endsWith(':30') || ora.endsWith(':45');
                        
                        return (
                          <div
                            key={ora}
                            className={`absolute w-full transition-colors ${
                              isHourMark ? 'border-t border-slate-300' : 
                              isQuarterMark ? 'border-t border-slate-200' : 
                              'border-t border-slate-100/30'
                            } ${isBlocked ? 'bg-amber-50/50 cursor-pointer hover:bg-amber-100/50' : 'cursor-pointer hover:bg-blue-50/40'}`}
                            style={{ top: `${idx * SLOT_HEIGHT}px`, height: `${SLOT_HEIGHT}px` }}
                            onClick={() => openNewAppointment(operatore.id, ora)}
                            title={isOperatoreOff ? `${ora} - Pausa operatore (clicca per prenotare comunque)` : `${ora} - Clicca per prenotare`}
                          >
                            {!isBlocked && (
                              <div className="absolute inset-0 flex items-center justify-center opacity-0 hover:opacity-100">
                                <Plus className="w-3 h-3 text-blue-300" />
                              </div>
                            )}
                            {isBlocked && (
                              <div className="absolute inset-0 flex items-center justify-center opacity-0 hover:opacity-100">
                                <Plus className="w-3 h-3 text-amber-500" />
                              </div>
                            )}
                          </div>
                        );
                      })}

                      {/* Blocchi operatore (ferie, pausa, etc.) - COMPACT */}
                      {opBlocchi.map(blocco => {
                        const tipo = TIPI_BLOCCO.find(t => t.id === blocco.tipo);
                        if (!tipo) return null;
                        const top = getTopPosition(blocco.ora_inizio);
                        const height = getHeight(
                          (parseInt(blocco.ora_fine.split(':')[0]) - parseInt(blocco.ora_inizio.split(':')[0])) * 60 +
                          (parseInt(blocco.ora_fine.split(':')[1]) - parseInt(blocco.ora_inizio.split(':')[1]))
                        );
                        
                        return (
                          <div
                            key={blocco.id}
                            className={`absolute left-0.5 right-0.5 ${tipo.color} text-white rounded-md opacity-50 z-5 cursor-pointer hover:opacity-70 transition-opacity`}
                            style={{ top: `${top}px`, height: `${height}px` }}
                            onClick={() => deleteBlocco(blocco.id)}
                            title={`${tipo.label} - Clicca per rimuovere`}
                          >
                            <div className="p-0.5 flex items-center gap-0.5">
                              <tipo.icon className="w-3 h-3" />
                              <span className="text-[9px] font-medium truncate">{tipo.label}</span>
                            </div>
                            {height > 32 && (
                              <div className="px-0.5 text-[8px] opacity-80">
                                {blocco.ora_inizio}-{blocco.ora_fine}
                              </div>
                            )}
                          </div>
                        );
                      })}

                      {/* Appuntamenti - Un blocco per ogni segmento di LAVORO (sequenza linear: lavoro1 -> posa1 -> lavoro2 -> posa2) */}
                      {(() => {
                        const slotsPosa = getSlotPosaForOperatore(operatore.id);

                        // 1) Raccogli tutti i segmenti che verranno renderizzati (per calcolare le "lane" da sovrapposizioni)
                        const allSegments = [];
                        opApps.filter(app => app.ora).forEach(app => {
                          const segmenti = getSegmentiAppuntamento(app);
                          segmenti.forEach((seg, segIdx) => {
                            allSegments.push({
                              app,
                              seg,
                              segIdx,
                              totaleSegmenti: segmenti.length,
                              start: seg.inizioMinuti,
                              end: seg.inizioMinuti + seg.durata,
                            });
                          });
                        });

                        // 2) Algoritmo greedy per assegnare "lane" (colonne) ai segmenti sovrapposti
                        //    Ordinati per start, ognuno riusa la prima lane libera.
                        allSegments.sort((a, b) => a.start - b.start || a.end - b.end);
                        const laneEnds = []; // laneEnds[i] = end time del segmento più recente nella lane i
                        allSegments.forEach(s => {
                          let lane = -1;
                          for (let i = 0; i < laneEnds.length; i++) {
                            if (laneEnds[i] <= s.start) { lane = i; break; }
                          }
                          if (lane === -1) { lane = laneEnds.length; laneEnds.push(s.end); }
                          else laneEnds[lane] = s.end;
                          s.laneIndex = lane;
                        });

                        // 3) Per ogni segmento calcolo il "gruppo di sovrapposizione" per determinare
                        //    quante lane servono NEL SUO SPAN temporale (evita di stringere quando non serve)
                        allSegments.forEach(s => {
                          let maxLane = s.laneIndex;
                          allSegments.forEach(other => {
                            if (other === s) return;
                            if (other.start < s.end && other.end > s.start) {
                              if (other.laneIndex > maxLane) maxLane = other.laneIndex;
                            }
                          });
                          s.laneCount = maxLane + 1;
                        });

                        // 4) Rendering
                        const cards = [];
                        allSegments.forEach((sg) => {
                          const app = sg.app;
                          const seg = sg.seg;
                          const segIdx = sg.segIdx;
                          const totaleSegmenti = sg.totaleSegmenti;
                          const laneIdx = sg.laneIndex;
                          const laneCnt = sg.laneCount;
                          const isPrimoSegmento = segIdx === 0;
                          const isUltimoSegmento = segIdx === totaleSegmenti - 1;
                          const topPos = getTopPosition(seg.oraInizio);
                          const heightCalc = getHeight(seg.durata);
                          const incassato = isIncassato(app);
                          const isExternal = app.is_external;
                          const isSlotPosa = app.is_slot_posa === true;
                          const slotPosaColor = '#f59e0b';

                          // Verifica overlap del SEGMENTO con pose di altri appuntamenti
                          const overlappingPosa = (() => {
                            for (const posa of slotsPosa) {
                              if (posa.appuntamentoId === app.id) continue;
                              if (seg.inizioMinuti < posa.fineMinuti && (seg.inizioMinuti + seg.durata) > posa.inizioMinuti) {
                                return posa;
                              }
                            }
                            return null;
                          })();

                          cards.push(
                            <AppuntamentoCard
                              key={`${app.id}-seg-${segIdx}`}
                              app={app}
                              operatore={operatore}
                              height={heightCalc}
                              top={topPos}
                              vistaCompatta={vistaCompatta}
                              incassato={incassato}
                              isSlotPosa={isSlotPosa}
                              slotPosaColor={slotPosaColor}
                              isExternal={isExternal}
                              draggingApp={draggingApp}
                              resizingApp={resizingApp}
                              verticalDragApp={verticalDragApp}
                              consulenzeStatus={consulenzeStatus}
                              overlappingPosa={overlappingPosa}
                              segmento={seg}
                              isPrimoSegmento={isPrimoSegmento}
                              isUltimoSegmento={isUltimoSegmento}
                              totaleSegmenti={totaleSegmenti}
                              laneIndex={laneIdx}
                              laneCount={laneCnt}
                              onDragStart={handleDragStart}
                              onDoubleClick={handleDoubleClick}
                              onConsulenzaClick={openConsulenzaDialog}
                              onSlotPosaClick={handleSlotPosaClick}
                              onResizeStart={handleResizeStart}
                              onVerticalDragStart={handleVerticalDragStart}
                              onToggleFissato={handleToggleFissato}
                              onClienteClick={handleClienteClickFromCard}
                              SLOT_HEIGHT={SLOT_HEIGHT}
                              minutiAssolutoToOra={minutiAssolutoToOra}
                            />
                          );
                        });
                        return cards;
                      })()}
                      
                      {/* Slot Posa - Blocchi separati proporzionali ai minuti */}
                      {(() => {
                        const slotsPosa = getSlotPosaForOperatore(operatore.id);
                        
                        return slotsPosa.map((slot) => {
                          const topPosa = getTopPosition(slot.oraInizioPosa);
                          const heightPosa = getHeight(slot.durataPosa);
                          
                          // Verifica se c'è un appuntamento che si sovrappone a questa posa
                          const hasOverlap = opApps.some(app => {
                            if (app.id === slot.appuntamentoId || !app.ora) return false;
                            const [h, m] = app.ora.split(':').map(Number);
                            const appInizio = h * 60 + m;
                            const appFine = appInizio + (app.durata_totale || 30);
                            return appInizio < slot.fineMinuti && appFine > slot.inizioMinuti;
                          });
                          
                          // Colore VIOLA/LAVANDA fisso per la posa
                          const posaColor = '#e9d5ff';
                          const posaBorderColor = '#a855f7';
                          
                          return (
                            <div
                              key={`posa-${slot.appuntamentoId}-${slot.servizioIndex}`}
                              data-testid={`slot-posa-${slot.appuntamentoId}-${slot.servizioIndex}`}
                              className={`absolute rounded-lg z-20 border-2 border-dashed shadow-md overflow-hidden pointer-events-none ${hasOverlap ? 'left-0.5' : 'left-0.5 right-0.5'}`}
                              style={{ 
                                top: `${topPosa}px`, 
                                height: `${Math.max(heightPosa, SLOT_HEIGHT)}px`,
                                width: hasOverlap ? '48%' : undefined,
                                background: `repeating-linear-gradient(
                                  45deg,
                                  ${posaColor},
                                  ${posaColor} 8px,
                                  #f3e8ff 8px,
                                  #f3e8ff 16px
                                )`,
                                borderColor: posaBorderColor
                              }}
                              title={`POSA: ${slot.clienteNome} - ${slot.durataPosa} min (${slot.oraInizioPosa} - ${slot.oraFinePosa})`}
                            >
                              <div className="h-full flex flex-col justify-center items-center px-1 py-0.5">
                                <div className="flex items-center gap-1 bg-white/80 px-1.5 py-0.5 rounded">
                                  <Hourglass className="w-3 h-3 text-violet-600" />
                                  <span className={`font-bold text-violet-700 ${vistaCompatta ? 'text-[9px]' : 'text-[11px]'}`}>
                                    POSA {slot.durataPosa}'
                                  </span>
                                </div>
                                
                                {heightPosa >= 30 && !hasOverlap && (
                                  <span className={`text-violet-600 truncate max-w-full bg-white/60 px-1 rounded mt-0.5 ${vistaCompatta ? 'text-[7px]' : 'text-[9px]'}`}>
                                    {slot.clienteNome}
                                  </span>
                                )}
                                
                                {heightPosa >= 40 && !hasOverlap && (
                                  <span className={`text-violet-500 ${vistaCompatta ? 'text-[6px]' : 'text-[8px]'}`}>
                                    {slot.oraInizioPosa} - {slot.oraFinePosa}
                                  </span>
                                )}
                              </div>
                            </div>
                          );
                        });
                      })()}
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        </CardContent>
        </>}
      </Card>

      {/* Legenda */}
      <div className="flex items-center justify-between mt-3 text-xs text-slate-500">
        <div className="flex items-center gap-3 flex-wrap">
          {operatoriVisibili.map(op => (
            <span key={op.id} className="flex items-center gap-1.5">
              <div className="w-3 h-3 rounded" style={{ backgroundColor: op.colore }}></div>
              {op.nome}
            </span>
          ))}
          <span className="flex items-center gap-1.5 ml-2">
            <div className="w-3 h-3 rounded bg-slate-300 relative">
              <CheckCircle2 className="w-2 h-2 text-green-500 absolute -top-0.5 -right-0.5" />
            </div>
            Incassato
          </span>
          {/* Legenda Posa Viola */}
          <span className="flex items-center gap-1.5 ml-2">
            <div className="w-4 h-4 rounded border-2 border-dashed border-violet-500" style={{
              background: 'repeating-linear-gradient(45deg, #e9d5ff, #e9d5ff 4px, #f3e8ff 4px, #f3e8ff 8px)'
            }}></div>
            Tempo Posa
          </span>
        </div>
        <p className="text-xs">
          <span className="font-medium">Doppio clic</span>: modifica appuntamento | 
          <span className="font-medium"> Clic sul nome</span>: profilo cliente | 
          <span className="font-medium"> Trascina</span>: cambia operatore
        </p>
      </div>

      {/* Quick Edit Dialog */}
      <Dialog open={quickEditOpen} onOpenChange={setQuickEditOpen}>
        <DialogContent
          className="w-[95vw] sm:max-w-md max-h-[88vh] p-0 overflow-hidden border-4 border-transparent bg-clip-padding shadow-2xl rounded-2xl flex flex-col"
          data-testid="quick-edit-dialog"
          style={{
            maxWidth: 'min(95vw, 480px)',
            backgroundImage: 'linear-gradient(white, white), linear-gradient(135deg, #3b82f6, #8b5cf6, #ec4899)',
            backgroundOrigin: 'border-box',
            backgroundClip: 'padding-box, border-box'
          }}
        >
          <DialogHeader className="px-5 pt-4 pb-3 bg-gradient-to-r from-blue-500 via-violet-500 to-pink-500 text-white">
            <DialogTitle className="flex items-center gap-2 text-white" style={{ fontFamily: 'Playfair Display, serif' }}>
              <Edit3 className="w-5 h-5" />
              Modifica Rapida
            </DialogTitle>
          </DialogHeader>
          
          {quickEditApp && (
            <div className="flex-1 overflow-y-auto px-5 pb-3 pt-4 space-y-4">
              <div className="p-4 bg-slate-50 rounded-lg space-y-2">
                <div className="flex items-center justify-between flex-wrap gap-2">
                  <div className="flex items-center gap-2 flex-wrap">
                    {quickEditApp.cliente_id ? (
                      <button
                        onClick={goToProfiloCliente}
                        data-testid="quickedit-nome-cliente-link"
                        className="font-bold text-lg text-blue-600 hover:text-blue-800 hover:underline text-left"
                        title="Vai al profilo cliente"
                      >
                        {quickEditApp.cliente_nome}
                      </button>
                    ) : (
                      <span className="font-bold text-lg">{quickEditApp.cliente_nome}</span>
                    )}
                    {quickEditApp.ricorrente && (
                      <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-indigo-100 text-indigo-700 rounded-full text-xs font-medium">
                        <Repeat className="w-3 h-3" />
                        Ricorrente
                      </span>
                    )}
                  </div>
                  <span className="text-sm text-slate-500">{quickEditApp.data}</span>
                </div>

                {quickEditApp.cliente_id && (
                  <div className="flex items-center gap-2">
                    <Button
                      onClick={goToProfiloCliente}
                      data-testid="quickedit-profilo-cliente-btn"
                      variant="outline"
                      size="sm"
                      className="flex-1 border-blue-200 text-blue-600 hover:bg-blue-50"
                    >
                      <User className="w-4 h-4 mr-2" />
                      Profilo Cliente
                    </Button>
                    <Button
                      onClick={goToModificaDatiCliente}
                      data-testid="quickedit-modifica-dati-cliente-btn"
                      variant="outline"
                      size="sm"
                      title="Modifica dati anagrafici (nome, telefono, data nascita...)"
                      className="border-slate-300 text-slate-700 hover:bg-slate-100 px-3"
                    >
                      <Settings className="w-4 h-4" />
                    </Button>
                  </div>
                )}
                
                {/* Sezione Servizi Modificabile */}
                <div className="bg-white rounded-lg p-2 border border-slate-200">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-xs font-semibold text-slate-500 flex items-center gap-1">
                      <Scissors className="w-3 h-3" /> Servizi
                    </span>
                    {!isIncassato(quickEditApp) && (
                      <Button 
                        size="sm" 
                        variant="outline" 
                        className="h-6 text-xs px-2"
                        onClick={() => setAddServizioOpen(!addServizioOpen)}
                      >
                        <Plus className="w-3 h-3 mr-1" /> Aggiungi
                      </Button>
                    )}
                  </div>
                  
                  {/* Popup per aggiungere servizio */}
                  {addServizioOpen && (
                    <div className="mb-2 p-2 bg-blue-50 rounded-lg border border-blue-200">
                      <Input
                        placeholder="Cerca servizio..."
                        value={searchServizioAgenda}
                        onChange={(e) => setSearchServizioAgenda(e.target.value)}
                        className="h-8 text-sm mb-2"
                        autoFocus
                      />
                      <div className="max-h-32 overflow-y-auto space-y-1">
                        {serviziFiltratAgenda.slice(0, 6).map(s => (
                          <button
                            key={s.id}
                            onClick={() => aggiungiServizioAppuntamento(s)}
                            className="w-full text-left p-2 hover:bg-blue-100 rounded-lg text-sm flex justify-between items-center"
                          >
                            <span className="truncate">{s.nome}</span>
                            <span className="text-blue-600 font-bold shrink-0 ml-2">€{s.prezzo}</span>
                          </button>
                        ))}
                        {serviziFiltratAgenda.length === 0 && (
                          <p className="text-center text-slate-400 text-xs py-2">Nessun servizio trovato</p>
                        )}
                      </div>
                    </div>
                  )}
                  
                  {/* Lista servizi attuali */}
                  <div className="space-y-1">
                    {quickEditApp.servizi?.map((s, idx) => (
                      <div key={idx} className="flex items-center justify-between py-1 px-2 bg-slate-50 rounded text-sm">
                        <span className="truncate flex-1">{s.nome}</span>
                        <div className="flex items-center gap-1 shrink-0">
                          <span className="font-semibold text-slate-700">€{s.prezzo}</span>
                          {!isIncassato(quickEditApp) && quickEditApp.servizi.length > 1 && (
                            <Button 
                              size="sm" 
                              variant="ghost" 
                              className="h-6 w-6 p-0 text-red-500 hover:text-red-700 hover:bg-red-50"
                              onClick={() => rimuoviServizioAppuntamento(idx)}
                            >
                              <X className="w-3 h-3" />
                            </Button>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
                
                <div className="flex items-center justify-between text-sm">
                  <span className="flex items-center gap-2">
                    <Clock className="w-4 h-4" />
                    {quickEditApp.ora} - {quickEditApp.ora_fine}
                  </span>
                  <span className="font-bold text-blue-600">€{quickEditApp.totale}</span>
                </div>
                {quickEditApp.operatore_nome && (
                  <p className="text-xs text-slate-500">
                    Operatore: {quickEditApp.operatore_nome}
                  </p>
                )}
              </div>

              {/* Pulsante Storico Cliente */}
              <Button 
                onClick={() => {
                  setStoricoClienteId(quickEditApp.cliente_id);
                  setStoricoClienteNome(quickEditApp.cliente_nome);
                  setStoricoOpen(true);
                }}
                variant="outline"
                className="w-full border-purple-200 text-purple-600 hover:bg-purple-50"
              >
                <History className="w-4 h-4 mr-2" />
                Storico Trattamenti
              </Button>

              {/* Pulsante Color Bar - solo per servizi colore/taglio/trattamento */}
              {(() => {
                // Usa configurazione se disponibile, altrimenti default
                const colorKeywords = configurazione?.colorbar_trattamenti || ['color', 'colore', 'tinta', 'gloss', 'tonalizzante', 'trattamento', 'taglio', 'meches', 'balayage', 'shatush', 'decolorazione', 'schiaritura'];
                const excludeKeywords = configurazione?.colorbar_esclusioni || ['piega', 'brushing', 'messa in piega'];
                
                const servizi = quickEditApp.servizi || [];
                const serviziNomi = servizi.map(s => (s.nome || '').toLowerCase()).join(' ');
                
                const hasColorService = colorKeywords.some(k => serviziNomi.includes(k.toLowerCase()));
                const hasExcludedOnly = excludeKeywords.some(k => serviziNomi.includes(k.toLowerCase())) && !hasColorService;
                
                const showColorBar = hasColorService && !hasExcludedOnly;
                
                return showColorBar ? (
                  <Button 
                    onClick={() => {
                      const cliente = clienti.find(c => c.id === quickEditApp.cliente_id);
                      if (cliente) {
                        setColorBarCliente(cliente);
                        setColorBarAppuntamento(quickEditApp);
                        setColorBarOpen(true);
                      } else {
                        setColorBarCliente({
                          id: quickEditApp.cliente_id,
                          nome: quickEditApp.cliente_nome?.split(' ')[0] || '',
                          cognome: quickEditApp.cliente_nome?.split(' ').slice(1).join(' ') || ''
                        });
                        setColorBarAppuntamento(quickEditApp);
                        setColorBarOpen(true);
                      }
                    }}
                    className="w-full bg-purple-600 hover:bg-purple-700 text-white"
                    size="lg"
                  >
                    <Palette className="w-5 h-5 mr-2" />
                    Color Bar - Trattamento Colore
                  </Button>
                ) : null;
              })()}

              {!isIncassato(quickEditApp) && (
                <>
                  <div className="space-y-3">
                    <Label className="text-sm font-medium">Modifica Durata</Label>
                    <div className="flex items-center justify-center gap-2">
                      <Button variant="outline" size="sm" onClick={() => adjustDuration(-15)}
                        disabled={(quickEditApp.durata_totale || 30) <= 15} className="h-10 px-4"
                        data-testid="quickedit-durata-minus">
                        <Minus className="w-4 h-4 mr-1" />15 min
                      </Button>
                      
                      <div className="px-4 py-2 bg-blue-100 rounded-lg text-center min-w-[80px]">
                        <p className="text-xl font-bold text-blue-600">{quickEditApp.durata_totale || 30}</p>
                        <p className="text-xs text-blue-500">minuti</p>
                      </div>
                      
                      <Button variant="outline" size="sm" onClick={() => adjustDuration(15)} className="h-10 px-4"
                        data-testid="quickedit-durata-plus">
                        <Plus className="w-4 h-4 mr-1" />15 min
                      </Button>
                    </div>
                  </div>

                  <div className="space-y-3">
                    <Label className="text-sm font-medium">Modifica Ora</Label>
                    <div className="flex items-center justify-center gap-2">
                      <Button variant="outline" size="sm" onClick={() => adjustOra(-15)}
                        className="h-10 px-4" data-testid="quickedit-ora-minus">
                        <Minus className="w-4 h-4 mr-1" />15 min
                      </Button>

                      <div className="px-4 py-2 bg-amber-100 rounded-lg text-center min-w-[110px]">
                        <p className="text-xl font-bold text-amber-700">{quickEditApp.ora}</p>
                        <p className="text-xs text-amber-600">orario</p>
                      </div>

                      <Button variant="outline" size="sm" onClick={() => adjustOra(15)}
                        className="h-10 px-4" data-testid="quickedit-ora-plus">
                        <Plus className="w-4 h-4 mr-1" />15 min
                      </Button>
                    </div>
                  </div>

                  <Button onClick={goToCassa} className="w-full bg-emerald-600 hover:bg-emerald-700 text-white" size="lg"
                    data-testid="quickedit-vai-cassa-btn">
                    <Banknote className="w-5 h-5 mr-2" />
                    Vai alla Cassa - Incassa €{quickEditApp.totale}
                  </Button>

                  <Button 
                    onClick={() => {
                      setQuickEditOpen(false);
                      // Delay per permettere a React di completare l'unmount del dialog
                      setTimeout(() => {
                        openSpostaAppuntamento(quickEditApp);
                      }, 100);
                    }} 
                    variant="outline"
                    className="w-full border-blue-200 text-blue-600 hover:bg-blue-50" 
                    size="lg"
                    data-testid="quickedit-modifica-giorno-btn"
                  >
                    <CalendarDays className="w-5 h-5 mr-2" />
                    Modifica Giorno
                  </Button>

                  {/* Pulsante Invia Promemoria */}
                  <Button 
                    onClick={inviaPromemoria}
                    disabled={invioPromemoriaLoading}
                    variant="outline"
                    className="w-full border-green-200 text-green-600 hover:bg-green-50" 
                    size="lg"
                  >
                    {invioPromemoriaLoading ? (
                      <>
                        <div className="w-5 h-5 mr-2 border-2 border-green-600 border-t-transparent rounded-full animate-spin" />
                        Invio in corso...
                      </>
                    ) : (
                      <>
                        <Send className="w-5 h-5 mr-2" />
                        Invia Promemoria (WhatsApp + Email)
                      </>
                    )}
                  </Button>
                  
                  {quickEditApp.promemoria_inviato && (
                    <p className="text-xs text-center text-green-600 flex items-center justify-center gap-1">
                      <CheckCircle2 className="w-3 h-3" />
                      Promemoria già inviato
                    </p>
                  )}
                </>
              )}

              {isIncassato(quickEditApp) && (
                <div className="p-4 bg-green-50 border border-green-200 rounded-lg">
                  <div className="flex items-center gap-2 text-green-700">
                    <CheckCircle2 className="w-5 h-5" />
                    <span className="font-medium">Appuntamento già incassato</span>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Action bar fissa in basso — sempre visibile */}
          {quickEditApp && (
            <div className="px-5 py-3 bg-white border-t border-slate-200 flex justify-between items-center gap-2 shadow-[0_-2px_8px_rgba(0,0,0,0.04)] rounded-b-xl">
              {!isIncassato(quickEditApp) ? (
                <Button
                  variant="destructive"
                  size="sm"
                  onClick={deleteAppointment}
                  data-testid="btn-delete-appointment"
                >
                  <Trash2 className="w-4 h-4 mr-2" />Elimina
                </Button>
              ) : <div></div>}
              <Button
                onClick={closeQuickEditAndScroll}
                className="bg-blue-600 hover:bg-blue-700"
                data-testid="btn-conferma-appointment"
              >
                <Check className="w-4 h-4 mr-2" />
                {isIncassato(quickEditApp) ? 'Chiudi' : 'Fatto'}
              </Button>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Dialog Ricerca Cliente Rapida - VERSIONE VELOCE */}
      <Dialog open={clienteSearchOpen} onOpenChange={(open) => {
        if (!open) closeClienteSearch();
        else setClienteSearchOpen(open);
      }}>
        <DialogContent
          className="w-[95vw] sm:max-w-lg max-h-[88vh] overflow-y-auto border-4 border-transparent bg-clip-padding shadow-2xl rounded-2xl"
          data-testid="cliente-search-dialog"
          style={{
            maxWidth: 'min(95vw, 560px)',
            backgroundImage: 'linear-gradient(white, white), linear-gradient(135deg, #3b82f6, #8b5cf6, #ec4899)',
            backgroundOrigin: 'border-box',
            backgroundClip: 'padding-box, border-box'
          }}
        >
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-lg" style={{ fontFamily: 'Playfair Display, serif' }}>
              <Search className="w-5 h-5 text-blue-600" />
              Cerca Cliente
            </DialogTitle>
          </DialogHeader>
          
          {/* Barra di ricerca */}
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
            <Input
              placeholder="Nome o cognome..."
              value={clienteSearchQuery}
              onChange={(e) => handleClienteSearch(e.target.value)}
              className="pl-10 h-10"
              autoFocus
              data-testid="cliente-search-input"
            />
          </div>
          
          {/* Risultati ricerca - Lista semplice */}
          {clienteSearchResults.length > 0 && !clienteSelezionato && (
            <div className="border rounded-lg max-h-80 overflow-y-auto divide-y">
              {clienteSearchResults.map(c => (
                <button
                  key={c.id}
                  onClick={() => loadClienteAppuntamenti(c)}
                  className="w-full px-3 py-2 text-left hover:bg-blue-50 flex items-center gap-3"
                >
                  <div className="w-8 h-8 rounded-full bg-blue-600 flex items-center justify-center text-white text-sm font-bold shrink-0">
                    {(c.nome?.[0] || '?').toUpperCase()}
                  </div>
                  <div className="min-w-0">
                    <p className="font-medium text-slate-800 truncate">{c.nome} {c.cognome || ''}</p>
                    <p className="text-xs text-slate-500">{c.telefono || ''}</p>
                  </div>
                </button>
              ))}
              {clienteSearchResults.length >= 50 && (
                <p className="text-xs text-center text-slate-400 py-2">Mostra i primi 50 risultati - affina la ricerca</p>
              )}
            </div>
          )}
          
          {/* Cliente selezionato - Vista appuntamenti */}
          {clienteSelezionato && (
            <div className="space-y-3">
              {/* Header cliente con azioni */}
              <div className="flex items-center justify-between p-3 bg-blue-50 rounded-lg">
                <div className="flex items-center gap-2">
                  <div className="w-10 h-10 rounded-full bg-blue-600 flex items-center justify-center text-white font-bold">
                    {(clienteSelezionato.nome?.[0] || '?').toUpperCase()}
                  </div>
                  <div>
                    <p className="font-bold">{clienteSelezionato.nome} {clienteSelezionato.cognome || ''}</p>
                    <p className="text-xs text-slate-500">{clienteSelezionato.telefono}</p>
                  </div>
                </div>
                <div className="flex items-center gap-1">
                  {/* Vai a Scheda Cliente */}
                  <Button 
                    variant="outline" 
                    size="sm" 
                    onClick={() => {
                      closeClienteSearch();
                      navigate(`/clienti?id=${clienteSelezionato.id}`);
                    }}
                    className="text-blue-600 border-blue-200 hover:bg-blue-100 h-8 px-2"
                    title="Apri scheda cliente"
                  >
                    <User className="w-4 h-4 mr-1" />
                    Scheda
                  </Button>
                  {/* Nuovo appuntamento */}
                  <Button 
                    variant="outline" 
                    size="sm" 
                    onClick={() => {
                      // Pre-compila form con cliente
                      setFormData(prev => ({
                        ...prev,
                        cliente_id: clienteSelezionato.id,
                        cliente_nome: `${clienteSelezionato.nome} ${clienteSelezionato.cognome || ''}`.trim(),
                        data: format(dataCorrente, 'yyyy-MM-dd')
                      }));
                      closeClienteSearch();
                      setDialogOpen(true);
                    }}
                    className="text-green-600 border-green-200 hover:bg-green-100 h-8 px-2"
                    title="Nuovo appuntamento"
                  >
                    <Plus className="w-4 h-4" />
                  </Button>
                  <Button variant="ghost" size="sm" onClick={() => {
                    setClienteSelezionato(null);
                    setClienteSearchQuery("");
                    setClienteAppuntamenti({ futuri: [], passati: [], ricorrenti: [] });
                  }} className="h-8 px-2">
                    <X className="w-4 h-4" />
                  </Button>
                </div>
              </div>
              
              {loadingClienteApp ? (
                <div className="py-8 text-center">
                  <Loader2 className="w-6 h-6 animate-spin mx-auto text-blue-600" />
                </div>
              ) : (
                <div className="max-h-72 overflow-y-auto space-y-2">
                  {/* Prossimi */}
                  {clienteAppuntamenti.futuri.length > 0 && (
                    <>
                      <p className="text-xs font-semibold text-green-700 px-1">📅 PROSSIMI ({clienteAppuntamenti.futuri.length})</p>
                      {clienteAppuntamenti.futuri.map(app => (
                        <button
                          key={app.id}
                          onClick={() => goToAppuntamento(app)}
                          className="w-full p-2 bg-green-50 hover:bg-green-100 rounded border border-green-200 text-left flex items-center justify-between"
                        >
                          <div>
                            <span className="font-bold text-green-800">{app.data.slice(8,10)}/{app.data.slice(5,7)}</span>
                            <span className="text-sm text-slate-600 ml-2">{app.ora} - {app.servizi?.map(s=>s.nome).join(', ')}</span>
                            {app.ricorrente && <Repeat className="inline w-3 h-3 ml-1 text-indigo-500" />}
                          </div>
                          <ArrowRight className="w-4 h-4 text-green-400" />
                        </button>
                      ))}
                    </>
                  )}
                  
                  {/* Passati */}
                  {clienteAppuntamenti.passati.length > 0 && (
                    <>
                      <p className="text-xs font-semibold text-slate-500 px-1 mt-2">🕐 PASSATI ({clienteAppuntamenti.passati.length})</p>
                      {clienteAppuntamenti.passati.slice(0,5).map(app => (
                        <button
                          key={app.id}
                          onClick={() => goToAppuntamento(app)}
                          className="w-full p-2 bg-slate-50 hover:bg-slate-100 rounded border border-slate-200 text-left flex items-center justify-between opacity-70"
                        >
                          <div>
                            <span className="font-medium text-slate-600">{app.data.slice(8,10)}/{app.data.slice(5,7)}</span>
                            <span className="text-sm text-slate-500 ml-2">{app.servizi?.map(s=>s.nome).join(', ')}</span>
                          </div>
                          <ArrowRight className="w-4 h-4 text-slate-300" />
                        </button>
                      ))}
                    </>
                  )}
                  
                  {/* Nessun appuntamento */}
                  {clienteAppuntamenti.futuri.length === 0 && clienteAppuntamenti.passati.length === 0 && (
                    <p className="text-center text-slate-500 py-4">Nessun appuntamento trovato</p>
                  )}
                </div>
              )}
            </div>
          )}
          
          {/* Stato iniziale */}
          {clienteSearchQuery.length < 1 && !clienteSelezionato && (
            <p className="text-center text-slate-400 py-6">Digita per cercare</p>
          )}
          
          {/* Nessun risultato */}
          {clienteSearchQuery.length >= 2 && clienteSearchResults.length === 0 && !clienteSelezionato && (
            <p className="text-center text-slate-500 py-6">Nessun cliente trovato</p>
          )}
        </DialogContent>
      </Dialog>

      {/* Dialog eliminazione appuntamento ricorrente */}
      <Dialog open={deleteRicorrenteOpen} onOpenChange={setDeleteRicorrenteOpen}>
        <DialogContent
          className="w-[95vw] max-w-md border-4 border-transparent bg-clip-padding shadow-2xl rounded-2xl"
          data-testid="delete-ricorrente-dialog"
          style={{
            maxWidth: 'min(95vw, 480px)',
            backgroundImage: 'linear-gradient(white, white), linear-gradient(135deg, #ef4444, #f59e0b, #ec4899)',
            backgroundOrigin: 'border-box',
            backgroundClip: 'padding-box, border-box'
          }}
        >
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Repeat className="w-5 h-5 text-indigo-600" />
              Eliminare Appuntamento Ricorrente?
            </DialogTitle>
          </DialogHeader>
          <div className="py-4 space-y-4">
            <p className="text-slate-600">
              Questo appuntamento fa parte di una serie ricorrente. Come vuoi procedere?
            </p>
            
            <div className="space-y-3">
              <Button
                variant="outline"
                className="w-full justify-start h-auto py-3 border-slate-200 hover:bg-slate-50"
                onClick={() => deleteAppointment(false)}
                data-testid="delete-single-btn"
              >
                <div className="text-left">
                  <p className="font-medium">Elimina solo questo</p>
                  <p className="text-xs text-slate-500">Gli appuntamenti futuri rimarranno</p>
                </div>
              </Button>
              
              <Button
                variant="outline"
                className="w-full justify-start h-auto py-3 border-red-200 hover:bg-red-50 text-red-600"
                onClick={() => deleteAppointment(true)}
                data-testid="delete-all-future-btn"
              >
                <div className="text-left">
                  <p className="font-medium">Elimina tutti i futuri</p>
                  <p className="text-xs text-red-400">Questo e tutti gli appuntamenti successivi</p>
                </div>
              </Button>
            </div>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setDeleteRicorrenteOpen(false)}>
              Annulla
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Dialog nuovo appuntamento */}
      <Dialog open={dialogOpen} onOpenChange={(open) => {
        setDialogOpen(open);
        if (!open) {
          setIsFromSlotPosa(false);
        }
      }}>
        <DialogContent className="w-[95vw] sm:max-w-lg max-h-[88vh] overflow-y-auto overflow-x-hidden p-4 sm:p-5 border-4 border-transparent bg-clip-padding shadow-2xl rounded-2xl agenda-dialog-scroll" style={{
          maxWidth: 'min(95vw, 560px)',
          backgroundImage: 'linear-gradient(white, white), linear-gradient(135deg, #3b82f6, #8b5cf6, #ec4899)',
          backgroundOrigin: 'border-box',
          backgroundClip: 'padding-box, border-box'
        }}>
          <DialogHeader>
            <DialogTitle style={{ fontFamily: 'Playfair Display, serif' }}>
              {isFromSlotPosa ? '📍 Appuntamento Slot Posa' : 'Nuovo Appuntamento'}
            </DialogTitle>
            {selectedSlot && (
              <p className="text-sm text-slate-500">
                {format(dataCorrente, "EEEE d MMMM yyyy", { locale: it })} alle {selectedSlot.ora}
                {formData.operatore_nome && ` - ${formData.operatore_nome}`}
              </p>
            )}
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4">
            {/* Bottone Conferma in alto — sempre visibile */}
            <Button
              type="submit"
              className="w-full bg-blue-600 hover:bg-blue-700 sticky top-0 z-10 shadow-md"
              disabled={!formData.cliente_id || formData.servizi.length === 0}
              data-testid="conferma-appuntamento-top"
            >
              Conferma Appuntamento
            </Button>
            <div className="space-y-2">
              <Label>Cliente</Label>
              <div className="relative">
                <Input
                  placeholder="Cerca cliente (min 2 caratteri)..."
                  value={searchCliente}
                  onChange={(e) => setSearchCliente(e.target.value)}
                  className="w-full"
                  autoComplete="off"
                />
                {searchCliente.length >= 2 && (
                  <div className="absolute z-50 w-full mt-1 bg-white border border-slate-200 rounded-lg shadow-lg max-h-60 overflow-y-auto">
                    {clientiFiltrati.map(c => (
                        <button
                          key={c.id}
                          type="button"
                          className="w-full px-3 py-2 text-left hover:bg-blue-50 flex items-center gap-3 border-b border-slate-100 last:border-0"
                          onClick={() => {
                            handleClienteChange(c.id);
                            setSearchCliente('');
                          }}
                        >
                          <div className="w-8 h-8 rounded-full bg-blue-600 text-white flex items-center justify-center text-xs font-bold">
                            {c.nome.charAt(0)}{(c.cognome || '').charAt(0)}
                          </div>
                          <div>
                            <p className="font-medium text-slate-800">{c.nome} {c.cognome}</p>
                            <p className="text-xs text-slate-500">{c.telefono}</p>
                          </div>
                        </button>
                      ))}
                    {clientiFiltrati.length === 0 && (
                      <div className="p-3 text-center">
                        <p className="text-sm text-slate-500 mb-2">Nessun cliente trovato</p>
                        <button
                          type="button"
                          onClick={() => {
                            setNuovoCliente({ nome: searchCliente, cognome: '', telefono: '' });
                            setNuovoClienteOpen(true);
                            setSearchCliente('');
                          }}
                          className="text-blue-600 text-sm font-medium hover:underline"
                        >
                          + Crea "{searchCliente}" come nuovo cliente
                        </button>
                      </div>
                    )}
                  </div>
                )}
              </div>
              
              {/* Pulsante Nuovo Cliente - PIÙ VISIBILE SU MOBILE */}
              {!formData.cliente_nome && !searchCliente && (
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => setNuovoClienteOpen(true)}
                  className="w-full border-2 border-dashed border-blue-300 text-blue-600 hover:bg-blue-50 hover:border-blue-400 py-3"
                  data-testid="agenda-nuovo-cliente-btn"
                >
                  <Plus className="w-5 h-5 mr-2" />
                  <span className="text-base font-medium">Aggiungi Nuovo Cliente</span>
                </Button>
              )}
              
              {/* Form nuovo cliente inline - MIGLIORATO PER MOBILE */}
              {nuovoClienteOpen && (
                <div className="p-4 bg-blue-50 rounded-xl border-2 border-blue-200 space-y-4" data-testid="agenda-nuovo-cliente-form">
                  <p className="text-base font-semibold text-blue-800 flex items-center gap-2">
                    <Plus className="w-5 h-5" />
                    Nuovo Cliente
                  </p>
                  <div className="space-y-3">
                    <Input
                      placeholder="Nome *"
                      value={nuovoCliente.nome}
                      onChange={(e) => setNuovoCliente({...nuovoCliente, nome: e.target.value})}
                      autoFocus
                      className="h-12 text-base"
                      data-testid="agenda-nuovo-cliente-nome"
                    />
                    <Input
                      placeholder="Cognome *"
                      value={nuovoCliente.cognome}
                      onChange={(e) => setNuovoCliente({...nuovoCliente, cognome: e.target.value})}
                      className="h-12 text-base"
                      data-testid="agenda-nuovo-cliente-cognome"
                    />
                    <Input
                      placeholder="Telefono * (obbligatorio)"
                      value={nuovoCliente.telefono}
                      onChange={(e) => setNuovoCliente({...nuovoCliente, telefono: e.target.value})}
                      className={`h-12 text-base ${!nuovoCliente.telefono ? 'border-red-300' : ''}`}
                      data-testid="agenda-nuovo-cliente-telefono"
                    />
                    <div className="flex gap-2" data-testid="agenda-nuovo-cliente-genere-toggle">
                      <button
                        type="button"
                        onClick={() => setNuovoCliente({...nuovoCliente, genere: 'F'})}
                        className={`flex-1 h-12 rounded-lg border-2 font-semibold transition-all ${
                          nuovoCliente.genere === 'F'
                            ? 'bg-pink-500 text-white border-pink-600 shadow-md'
                            : 'bg-white text-pink-600 border-pink-300 hover:bg-pink-50'
                        }`}
                        data-testid="agenda-nuovo-cliente-donna"
                      >
                        👩 Donna *
                      </button>
                      <button
                        type="button"
                        onClick={() => setNuovoCliente({...nuovoCliente, genere: 'M'})}
                        className={`flex-1 h-12 rounded-lg border-2 font-semibold transition-all ${
                          nuovoCliente.genere === 'M'
                            ? 'bg-blue-500 text-white border-blue-600 shadow-md'
                            : 'bg-white text-blue-600 border-blue-300 hover:bg-blue-50'
                        }`}
                        data-testid="agenda-nuovo-cliente-uomo"
                      >
                        👨 Uomo *
                      </button>
                    </div>
                  </div>
                  <div className="flex gap-3">
                    <Button 
                      type="button" 
                      variant="outline" 
                      onClick={() => {
                        setNuovoClienteOpen(false);
                        setNuovoCliente({ nome: '', cognome: '', telefono: '', genere: '' });
                      }}
                      className="flex-1 h-11"
                      data-testid="agenda-nuovo-cliente-annulla"
                    >
                      Annulla
                    </Button>
                    <Button 
                      type="button"
                      onClick={handleCreaNuovoCliente}
                      className="flex-1 h-11 bg-blue-600 hover:bg-blue-700 text-base font-semibold"
                      data-testid="agenda-nuovo-cliente-crea"
                    >
                      Crea e Seleziona
                    </Button>
                  </div>
                </div>
              )}
              {formData.cliente_nome && (
                <div className="flex items-center gap-2 p-2 bg-blue-50 rounded-lg" data-testid="cliente-selezionato-box">
                  <div className="w-8 h-8 rounded-full bg-blue-600 text-white flex items-center justify-center text-xs font-bold">
                    {formData.cliente_nome.split(' ').map(n => n.charAt(0)).join('').slice(0, 2)}
                  </div>
                  <span className="font-medium text-blue-800 flex-1">{formData.cliente_nome}</span>
                  {formData.cliente_id && (
                    <button
                      type="button"
                      onClick={() => {
                        const returnState = {
                          fromAgenda: true,
                          data: format(dataCorrente, 'yyyy-MM-dd'),
                          appuntamentoId: formData?.id || null,
                          // Salva i dati del form per ripristinarli al ritorno
                          pendingAppuntamento: {
                            ...formData,
                            data: format(dataCorrente, 'yyyy-MM-dd')
                          }
                        };
                        // Persisti anche in sessionStorage per resistenza
                        try { sessionStorage.setItem('pendingAppuntamento', JSON.stringify(returnState.pendingAppuntamento)); } catch (e) { console.warn('sessionStorage:', e); }
                        setDialogOpen(false);
                        navigate(`/clienti?clienteId=${formData.cliente_id}`, { state: returnState });
                      }}
                      className="text-blue-600 hover:text-white hover:bg-blue-600 transition rounded-md px-2 py-1 text-xs font-semibold border border-blue-300 hover:border-blue-600 flex items-center gap-1"
                      title="Apri profilo cliente"
                      data-testid="btn-apri-profilo-cliente-form"
                    >
                      <User className="w-3 h-3" /> Profilo
                    </button>
                  )}
                  <button
                    type="button"
                    onClick={() => setFormData({ ...formData, cliente_id: '', cliente_nome: '' })}
                    className="ml-1 text-blue-600 hover:text-blue-800"
                    title="Rimuovi cliente"
                  >
                    <X className="w-4 h-4" />
                  </button>
                </div>
              )}
            </div>
            
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Ora Inizio *</Label>
                <Select value={formData.ora} onValueChange={(v) => setFormData({ ...formData, ora: v })}>
                  <SelectTrigger data-testid="select-ora">
                    <SelectValue placeholder="Seleziona ora" />
                  </SelectTrigger>
                  <SelectContent>
                    {ORARI_SALONE.map(ora => (
                      <SelectItem key={ora} value={ora}>{ora}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Operatore *</Label>
                <Select 
                  value={formData.operatore_id} 
                  onValueChange={(v) => {
                    const op = operatori.find(o => o.id === v);
                    setFormData({ 
                      ...formData, 
                      operatore_id: v,
                      operatore_nome: op ? `${op.nome} ${op.cognome || ''}`.trim() : ''
                    });
                  }}
                >
                  <SelectTrigger data-testid="select-operatore">
                    <SelectValue placeholder="Seleziona operatore" />
                  </SelectTrigger>
                  <SelectContent>
                    {operatori.filter(o => o.attivo !== false).map(op => (
                      <SelectItem key={op.id} value={op.id}>
                        <span className="flex items-center gap-2">
                          <span className="w-3 h-3 rounded-full" style={{ backgroundColor: op.colore }} />
                          {op.nome} {op.cognome}
                        </span>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            {/* Servizi - nascosti se da slot posa */}
            {isFromSlotPosa ? (
              <div className="p-4 bg-amber-50 border border-amber-200 rounded-lg">
                <div className="flex items-center gap-2 mb-2">
                  <Lightbulb className="w-5 h-5 text-amber-600" />
                  <span className="font-medium text-amber-800">Servizio da Slot Posa</span>
                </div>
                <div className="bg-white p-3 rounded-lg border border-amber-100">
                  {formData.servizi.map((s, i) => (
                    <div key={s.id || `servizio-slot-${i}`} className="flex justify-between items-center">
                      <span className="font-medium">{s.nome}</span>
                      <div className="text-sm text-slate-500">
                        {s.durata_minuti}min • €{s.prezzo}
                      </div>
                    </div>
                  ))}
                </div>
                <p className="text-xs text-amber-600 mt-2">
                  Questo servizio è stato scelto per riempire il tempo di posa
                </p>
              </div>
            ) : (
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <Label>Servizi</Label>
                {/* Mostra selettore categoria solo se il cliente non ha genere specificato */}
                {!genereCliente && (
                  <div className="flex gap-1">
                    {["donna", "uomo"].map(cat => (
                      <button
                        key={cat}
                        type="button"
                        onClick={() => setFilterCategoria(cat)}
                        className={`px-3 py-1 rounded-full text-xs font-medium transition-colors ${
                          filterCategoria === cat 
                            ? cat === 'donna' ? 'bg-pink-500 text-white' : 'bg-blue-600 text-white'
                            : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
                        }`}
                      >
                        {cat === 'donna' ? '👩 Donna' : '👨 Uomo'}
                      </button>
                    ))}
                  </div>
                )}
                </div>
                <p className={`text-xs ${genereCliente ? 'font-medium' : ''} ${
                  categoriaServiziPerCliente === 'donna' ? 'text-pink-500' : 'text-blue-600'
                }`}>
                  {genereCliente 
                    ? `${genereCliente === 'F' ? '👩' : '👨'} Cliente ${genereCliente === 'F' ? 'Donna' : 'Uomo'} → Solo servizi ${categoriaServiziPerCliente === 'donna' ? 'Donna' : 'Uomo'} + Unisex`
                    : `${categoriaServiziPerCliente === 'donna' ? 'Servizi Donna' : 'Servizi Uomo'} + Unisex`
                  }
                </p>
                {/* === Selezione servizi raggruppati per CATEGORIA con ricerca e tab === */}
                <ServiziSelector
                  servizi={filteredServizi}
                  selectedServizi={formData.servizi}
                  onToggle={handleServizioToggle}
                />

              
              {formData.servizi.length > 0 && (
                <div className="space-y-2">
                  {/* Lista servizi selezionati con colori */}
                  <div className="p-2 bg-slate-50 rounded-lg space-y-1">
                    <Label className="text-xs text-slate-500">Servizi selezionati:</Label>
                    {formData.servizi.map((s, i) => (
                      <div 
                        key={s.nome + i} 
                        className="flex items-center justify-between p-1.5 bg-white rounded border border-slate-200"
                      >
                        <div className="flex items-center gap-2">
                          <span 
                            className="w-3 h-3 rounded-full shrink-0 ring-1 ring-white shadow-sm"
                            style={{ backgroundColor: s.colore || '#3B82F6' }}
                          />
                          <span className="text-sm font-medium">{s.nome}</span>
                          {s.is_associato && (
                            <span className="text-[10px] bg-amber-100 text-amber-700 px-1.5 py-0.5 rounded-full">
                              auto
                            </span>
                          )}
                        </div>
                        <div className="flex items-center gap-2">
                          <span className="text-xs text-slate-500">{s.durata_minuti}' • €{s.prezzo}</span>
                          <button
                            type="button"
                            onClick={() => setFormData({
                              ...formData,
                              servizi: formData.servizi.filter((_, idx) => idx !== i)
                            })}
                            className="text-red-400 hover:text-red-600 p-0.5"
                          >
                            <X className="w-3.5 h-3.5" />
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                  
                  {/* Cliente Nuovo - Stellina */}
                  {!isFromSlotPosa && (
                    <div className="p-3 bg-gradient-to-r from-amber-50 to-yellow-50 rounded-lg space-y-3 border border-amber-200" data-testid="cliente-nuovo-section">
                      <div className="flex items-center justify-between">
                        <Label className="flex items-center gap-2 cursor-pointer text-sm font-medium">
                          <Star className="w-4 h-4 text-amber-500" />
                          Cliente Nuovo
                        </Label>
                        <input
                          type="checkbox"
                          data-testid="cliente-nuovo-checkbox"
                          checked={formData.cliente_nuovo || false}
                          onChange={(e) => {
                            setFormData({
                              ...formData,
                              cliente_nuovo: e.target.checked,
                              canale_acquisizione: e.target.checked ? 'passaparola' : null
                            });
                          }}
                          className="w-4 h-4 text-amber-500 rounded focus:ring-amber-500"
                        />
                      </div>
                      
                      {formData.cliente_nuovo && (
                        <div className="pt-2">
                          <Label className="text-xs text-slate-600 mb-1 block">Come ci ha conosciuto?</Label>
                          <Select
                            value={formData.canale_acquisizione || "passaparola"}
                            onValueChange={(v) => setFormData({...formData, canale_acquisizione: v})}
                          >
                            <SelectTrigger className="h-8 bg-white text-sm" data-testid="canale-select">
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="passaparola">
                                <span className="flex items-center gap-2">
                                  <Users className="w-3.5 h-3.5 text-blue-500" />
                                  Passaparola
                                </span>
                              </SelectItem>
                              <SelectItem value="social">
                                <span className="flex items-center gap-2">
                                  <Share2 className="w-3.5 h-3.5 text-pink-500" />
                                  Social Media
                                </span>
                              </SelectItem>
                              <SelectItem value="google">
                                <span className="flex items-center gap-2">
                                  <Search className="w-3.5 h-3.5 text-green-500" />
                                  Google/Ricerca
                                </span>
                              </SelectItem>
                              <SelectItem value="volantino">
                                <span className="flex items-center gap-2">
                                  <FileText className="w-3.5 h-3.5 text-orange-500" />
                                  Volantino
                                </span>
                              </SelectItem>
                              <SelectItem value="altro">
                                <span className="flex items-center gap-2">
                                  <HelpCircle className="w-3.5 h-3.5 text-slate-500" />
                                  Altro
                                </span>
                              </SelectItem>
                            </SelectContent>
                          </Select>
                        </div>
                      )}
                    </div>
                  )}
                  
                  {/* Appuntamento Ricorrente */}
                  {!isFromSlotPosa && (
                    <div className="p-3 bg-gradient-to-r from-indigo-50 to-purple-50 rounded-lg space-y-3 border border-indigo-200" data-testid="ricorrente-section">
                      <div className="flex items-center justify-between">
                        <Label className="flex items-center gap-2 cursor-pointer text-sm font-medium">
                          <Repeat className="w-4 h-4 text-indigo-600" />
                          Appuntamento Ricorrente
                        </Label>
                        <input
                          type="checkbox"
                          data-testid="ricorrente-checkbox"
                          checked={formData.ricorrente || false}
                          onChange={(e) => {
                            const checked = e.target.checked;
                            // Calcola data fine default (3 mesi)
                            const dataFineDefault = new Date(dataCorrente);
                            dataFineDefault.setMonth(dataFineDefault.getMonth() + 3);
                            
                            setFormData({
                              ...formData,
                              ricorrente: checked,
                              ricorrenza_tipo: checked ? "settimanale" : null,
                              ricorrenza_fine: checked ? format(dataFineDefault, 'yyyy-MM-dd') : null
                            });
                          }}
                          className="w-4 h-4 text-indigo-600 rounded focus:ring-indigo-500"
                        />
                      </div>
                      
                      {formData.ricorrente && (
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 pt-2">
                          <div>
                            <Label className="text-xs text-slate-600 mb-1 block">Frequenza</Label>
                            <Select
                              value={formData.ricorrenza_tipo || "settimanale"}
                              onValueChange={(v) => setFormData({...formData, ricorrenza_tipo: v})}
                            >
                              <SelectTrigger className="h-8 bg-white text-sm" data-testid="ricorrenza-tipo-select">
                                <SelectValue />
                              </SelectTrigger>
                              <SelectContent>
                                <SelectItem value="settimanale">Ogni settimana</SelectItem>
                                <SelectItem value="bisettimanale">Ogni 2 settimane</SelectItem>
                                <SelectItem value="mensile">Ogni mese</SelectItem>
                              </SelectContent>
                            </Select>
                          </div>
                          <div>
                            <Label className="text-xs text-slate-600 mb-1 block">Fino al</Label>
                            <Input
                              type="date"
                              data-testid="ricorrenza-fine-input"
                              value={formData.ricorrenza_fine || ""}
                              onChange={(e) => setFormData({...formData, ricorrenza_fine: e.target.value})}
                              className="h-8 bg-white text-sm"
                              min={format(dataCorrente, 'yyyy-MM-dd')}
                            />
                          </div>
                          <div className="col-span-2">
                            <p className="text-xs text-indigo-600 flex items-center gap-1">
                              <CalendarDays className="w-3 h-3" />
                              {formData.ricorrenza_tipo === "settimanale" && "Appuntamento ogni settimana"}
                              {formData.ricorrenza_tipo === "bisettimanale" && "Appuntamento ogni 2 settimane"}
                              {formData.ricorrenza_tipo === "mensile" && "Appuntamento ogni mese"}
                            </p>
                          </div>
                        </div>
                      )}
                    </div>
                  )}
                  
                  {/* Riepilogo */}
                  <div className="p-3 bg-blue-50 rounded-lg space-y-2">
                    <div className="flex justify-between items-center">
                      <span className="text-sm font-medium text-slate-600">Durata:</span>
                      <span className="font-bold text-blue-600">{durataTotale} min</span>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-sm font-medium text-slate-600">Orario:</span>
                      <span className="font-medium text-slate-800">{formData.ora} → {calcolaOraFine()}</span>
                    </div>
                    <div className="flex justify-between items-center pt-2 border-t border-blue-200">
                      <span className="text-sm font-medium text-slate-600">Totale:</span>
                      <span className="text-xl font-bold text-blue-600">€{totaleServizi.toFixed(2)}</span>
                    </div>
                  </div>
                </div>
              )}
              </div>
            )}
          </form>
        </DialogContent>
      </Dialog>

      {/* Dialog Sposta Appuntamento a Altro Giorno */}
      <Dialog open={spostaAppOpen} onOpenChange={setSpostaAppOpen}>
        <DialogContent className="sm:max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <CalendarDays className="w-5 h-5 text-blue-600" />
              Sposta Appuntamento
            </DialogTitle>
          </DialogHeader>
          
          {appDaSpostare && (
            <div className="space-y-5">
              {/* Info cliente */}
              <div className="p-4 bg-gradient-to-r from-blue-50 to-indigo-50 rounded-xl border border-blue-100">
                <p className="font-bold text-lg text-slate-800">{appDaSpostare.cliente_nome}</p>
                <p className="text-sm text-slate-500">
                  Attualmente: {appDaSpostare.data} alle {appDaSpostare.ora}
                </p>
              </div>

              {/* Seleziona nuova data */}
              <div className="space-y-2">
                <Label className="flex items-center gap-2 font-semibold">
                  <CalendarDays className="w-4 h-4 text-blue-600" />
                  Nuova Data
                </Label>
                <Popover>
                  <PopoverTrigger asChild>
                    <Button variant="outline" className="w-full justify-start font-normal">
                      <CalendarDays className="mr-2 h-4 w-4" />
                      {spostaFormData.nuovaData 
                        ? format(spostaFormData.nuovaData, "EEEE d MMMM yyyy", { locale: it })
                        : "Seleziona data"}
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-auto p-0" align="start">
                    <CalendarComponent
                      mode="single"
                      selected={spostaFormData.nuovaData}
                      onSelect={(date) => setSpostaFormData({...spostaFormData, nuovaData: date})}
                      locale={it}
                    />
                  </PopoverContent>
                </Popover>
              </div>

              {/* Seleziona nuova ora */}
              <div className="space-y-2">
                <Label className="flex items-center gap-2 font-semibold">
                  <Clock className="w-4 h-4 text-blue-600" />
                  Nuova Ora
                </Label>
                <Select 
                  value={spostaFormData.nuovaOra} 
                  onValueChange={(v) => setSpostaFormData({...spostaFormData, nuovaOra: v})}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Seleziona ora" />
                  </SelectTrigger>
                  <SelectContent>
                    {ORARI_SALONE.map(o => (
                      <SelectItem key={o} value={o}>{o}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* Seleziona operatore */}
              <div className="space-y-2">
                <Label className="flex items-center gap-2 font-semibold">
                  <User className="w-4 h-4 text-blue-600" />
                  Operatore
                </Label>
                <Select 
                  value={spostaFormData.nuovoOperatoreId} 
                  onValueChange={(v) => setSpostaFormData({...spostaFormData, nuovoOperatoreId: v})}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Seleziona operatore" />
                  </SelectTrigger>
                  <SelectContent>
                    {operatoriAttivi.map(op => (
                      <SelectItem key={op.id} value={op.id}>
                        {op.nome} {op.cognome || ''}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* Modifica servizi */}
              <div className="space-y-2">
                <Label className="flex items-center gap-2 font-semibold">
                  <Edit3 className="w-4 h-4 text-blue-600" />
                  Servizi (modifica se necessario)
                </Label>
                
                {/* Servizi selezionati */}
                <div className="space-y-2">
                  {spostaFormData.serviziModificati.map((s) => (
                    <div key={s.id || s.nome} className="flex items-center justify-between p-2 bg-blue-50 rounded-lg border border-blue-100">
                      <div className="flex items-center gap-2">
                        <div 
                          className="w-3 h-3 rounded-full" 
                          style={{ backgroundColor: s.colore || '#3B82F6' }}
                        />
                        <span className="font-medium">{s.nome}</span>
                        <span className="text-sm text-slate-500">({s.durata_minuti}min)</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <span className="font-medium text-blue-600">€{s.prezzo}</span>
                        <Button 
                          variant="ghost" 
                          size="sm" 
                          onClick={() => removeServizioSpostamento(s.id)}
                          className="h-6 w-6 p-0 text-red-500 hover:text-red-700 hover:bg-red-50"
                        >
                          <X className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>

                {/* Aggiungi altri servizi */}
                <div className="border rounded-lg p-2 max-h-32 overflow-y-auto space-y-1">
                  {servizi
                    .filter(s => s.attivo !== false && !spostaFormData.serviziModificati.find(x => x.id === s.id))
                    .slice(0, 15)
                    .map(s => (
                      <button
                        key={s.id}
                        type="button"
                        onClick={() => addServizioSpostamento(s)}
                        className="flex items-center justify-between w-full px-2 py-1.5 hover:bg-slate-100 rounded text-sm"
                      >
                        <div className="flex items-center gap-2">
                          <div 
                            className="w-2 h-2 rounded-full" 
                            style={{ backgroundColor: s.colore || '#3B82F6' }}
                          />
                          <span>{s.nome}</span>
                        </div>
                        <span className="text-slate-500">{s.durata_minuti}min • €{s.prezzo}</span>
                      </button>
                    ))}
                </div>
              </div>

              {/* Riepilogo */}
              <div className="p-4 bg-slate-50 rounded-xl space-y-2">
                <div className="flex justify-between text-sm">
                  <span className="text-slate-500">Durata totale:</span>
                  <span className="font-medium">
                    {spostaFormData.serviziModificati.reduce((acc, s) => acc + (s.durata_minuti || 30), 0)} min
                  </span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-slate-500">Totale:</span>
                  <span className="font-bold text-blue-600">
                    €{spostaFormData.serviziModificati.reduce((acc, s) => acc + (s.prezzo || 0), 0).toFixed(2)}
                  </span>
                </div>
              </div>
            </div>
          )}

          <DialogFooter className="gap-2 mt-4">
            <Button variant="outline" onClick={() => setSpostaAppOpen(false)}>
              Annulla
            </Button>
            <Button 
              onClick={salvaSpostaAppuntamento}
              className="bg-blue-600 hover:bg-blue-700"
              disabled={!spostaFormData.nuovaData || spostaFormData.serviziModificati.length === 0}
            >
              <Check className="w-4 h-4 mr-2" />
              Conferma Spostamento
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Dialog conferma spostamento operatore */}
      <Dialog open={confirmMoveOpen} onOpenChange={setConfirmMoveOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <ArrowRightLeft className="w-5 h-5 text-blue-600" />
              Conferma Spostamento
            </DialogTitle>
          </DialogHeader>
          <div className="py-4">
            <p className="text-slate-600">
              Vuoi spostare l'appuntamento di{' '}
              <span className="font-semibold text-slate-800">
                {pendingMove?.appuntamento?.cliente_nome}
              </span>{' '}
              all'operatore{' '}
              <span className="font-semibold text-blue-600">
                {pendingMove?.nuovoOperatoreNome}
              </span>?
            </p>
          </div>
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={cancelMoveAppuntamento}>
              No, Annulla
            </Button>
            <Button 
              onClick={confirmMoveAppuntamento}
              className="bg-blue-600 hover:bg-blue-700"
            >
              Sì, Sposta
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Dialog blocco operatore */}
      <Dialog open={bloccoDialogOpen} onOpenChange={setBloccoDialogOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>
              {TIPI_BLOCCO.find(t => t.id === bloccoData.tipo)?.label || 'Aggiungi Info'}
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={handleBloccoSubmit} className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Ora Inizio</Label>
                <Input
                  type="time"
                  value={bloccoData.ora_inizio}
                  onChange={(e) => setBloccoData({ ...bloccoData, ora_inizio: e.target.value })}
                  required
                />
              </div>
              <div className="space-y-2">
                <Label>Ora Fine</Label>
                <Input
                  type="time"
                  value={bloccoData.ora_fine}
                  onChange={(e) => setBloccoData({ ...bloccoData, ora_fine: e.target.value })}
                  required
                />
              </div>
            </div>
            
            <div className="space-y-2">
              <Label>Note (opzionale)</Label>
              <Input
                value={bloccoData.note}
                onChange={(e) => setBloccoData({ ...bloccoData, note: e.target.value })}
                placeholder="Aggiungi note..."
              />
            </div>

            <div className="flex gap-3">
              <Button type="button" variant="outline" onClick={() => setBloccoDialogOpen(false)} className="flex-1">
                Annulla
              </Button>
              <Button type="submit" className="flex-1 bg-blue-600 hover:bg-blue-700">
                Aggiungi
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Dialog Consulenza Rapida */}
      <Dialog open={consulenzaDialogOpen} onOpenChange={setConsulenzaDialogOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <FileText className="w-5 h-5 text-red-500" />
              Carica Consulenza
            </DialogTitle>
          </DialogHeader>

          <div className="space-y-5 pt-4">
            {/* Info Cliente */}
            {consulenzaCliente && (
              <div className="flex items-center gap-3 p-3 bg-blue-50 rounded-lg">
                <div className="w-10 h-10 bg-blue-500 rounded-full flex items-center justify-center text-white font-bold">
                  {consulenzaCliente.nome?.charAt(0)?.toUpperCase() || 'C'}
                </div>
                <div>
                  <p className="font-semibold text-slate-800">{consulenzaCliente.nome}</p>
                  <p className="text-xs text-slate-500">Carica la scheda consulenza PDF</p>
                </div>
              </div>
            )}

            {/* Upload File */}
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-2">
                File PDF *
              </label>
              <div 
                className={`border-2 border-dashed rounded-xl p-6 text-center transition-colors ${
                  consulenzaFile ? 'border-green-400 bg-green-50' : 'border-slate-200 hover:border-blue-300'
                }`}
              >
                <input
                  type="file"
                  accept=".pdf"
                  onChange={handleConsulenzaFileChange}
                  className="hidden"
                  id="consulenza-upload-agenda"
                />
                <label htmlFor="consulenza-upload-agenda" className="cursor-pointer block">
                  {consulenzaFile ? (
                    <div className="flex items-center justify-center gap-3">
                      <FileText className="w-8 h-8 text-red-500" />
                      <div className="text-left">
                        <p className="font-medium text-slate-800">{consulenzaFile.name}</p>
                        <p className="text-xs text-slate-500">
                          {(consulenzaFile.size / 1024 / 1024).toFixed(2)} MB
                        </p>
                      </div>
                      <CheckCircle2 className="w-5 h-5 text-green-500" />
                    </div>
                  ) : (
                    <>
                      <Upload className="w-10 h-10 text-slate-400 mx-auto mb-2" />
                      <p className="text-slate-600 font-medium">Clicca per selezionare PDF</p>
                      <p className="text-xs text-slate-400 mt-1">Max 10MB</p>
                    </>
                  )}
                </label>
              </div>
            </div>

            {/* Descrizione */}
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-2">
                Descrizione (opzionale)
              </label>
              <Input
                value={consulenzaDescrizione}
                onChange={(e) => setConsulenzaDescrizione(e.target.value)}
                placeholder="Es: Consulenza colore iniziale"
              />
            </div>

            {/* Azioni */}
            <div className="flex gap-3 pt-2">
              <Button 
                variant="outline" 
                onClick={() => {
                  setConsulenzaDialogOpen(false);
                  setConsulenzaFile(null);
                  setConsulenzaCliente(null);
                  setConsulenzaDescrizione("");
                }} 
                className="flex-1"
              >
                Annulla
              </Button>
              <Button 
                onClick={handleUploadConsulenza} 
                disabled={consulenzaUploading || !consulenzaFile}
                className="flex-1 bg-red-500 hover:bg-red-600 gap-2"
              >
                {consulenzaUploading ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin" />
                    Caricamento...
                  </>
                ) : (
                  <>
                    <Upload className="w-4 h-4" />
                    Carica Consulenza
                  </>
                )}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Color Bar Dialog - Versione PRO */}
      <ColorBarPro
        cliente={colorBarCliente}
        appuntamento={colorBarAppuntamento}
        operatori={operatori}
        open={colorBarOpen}
        onOpenChange={setColorBarOpen}
        onSuccess={() => {
          setColorBarOpen(false);
          setColorBarCliente(null);
          setColorBarAppuntamento(null);
          toast.success("Trattamento colore salvato!");
        }}
      />

      {/* Dialog Info Posa */}
      <Dialog open={posaInfoOpen} onOpenChange={setPosaInfoOpen}>
        <DialogContent className="max-w-sm" data-testid="posa-info-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-amber-600">
              <Hourglass className="w-5 h-5" />
              Tempo di Posa
            </DialogTitle>
          </DialogHeader>
          
          {posaInfoData && (
            <div className="space-y-4">
              {/* Info Cliente */}
              <div className="bg-amber-50 rounded-xl p-4 border border-amber-200">
                <div className="flex items-center gap-3 mb-3">
                  <div className="w-10 h-10 rounded-full bg-amber-100 flex items-center justify-center">
                    <User className="w-5 h-5 text-amber-600" />
                  </div>
                  <div>
                    <p className="font-semibold text-slate-800">{posaInfoData.clienteNome || 'Cliente'}</p>
                    <p className="text-xs text-slate-500">Cliente in posa</p>
                  </div>
                </div>
                
                {/* Dettagli Posa */}
                <div className="grid grid-cols-2 gap-3 text-sm">
                  <div className="bg-white rounded-lg p-2 border border-amber-100">
                    <p className="text-xs text-slate-500">Durata Posa</p>
                    <p className="font-bold text-amber-600 text-lg">{posaInfoData.durataPosa || 0} min</p>
                  </div>
                  <div className="bg-white rounded-lg p-2 border border-amber-100">
                    <p className="text-xs text-slate-500">Orario</p>
                    <p className="font-bold text-slate-700">{posaInfoData.oraInizioPosa || '--:--'} - {posaInfoData.oraFinePosa || '--:--'}</p>
                  </div>
                </div>
              </div>
              
              {/* Servizi con Posa */}
              {posaInfoData.servizi?.length > 0 && (
                <div>
                  <p className="text-xs text-slate-500 mb-2 font-medium">Servizi in posa:</p>
                  <div className="space-y-1">
                    {posaInfoData.servizi.filter(s => (s.tempo_posa || 0) > 0).map((servizio, idx) => (
                      <div 
                        key={`posa-serv-${idx}`}
                        className="flex items-center justify-between p-2 bg-slate-50 rounded-lg"
                      >
                        <div className="flex items-center gap-2">
                          <div 
                            className="w-3 h-3 rounded-full"
                            style={{ backgroundColor: servizio.colore || '#f59e0b' }}
                          />
                          <span className="text-sm font-medium">{servizio.nome || 'Servizio'}</span>
                        </div>
                        <Badge variant="outline" className="bg-amber-50 text-amber-700 border-amber-200">
                          {servizio.tempo_posa || 0}' posa
                        </Badge>
                      </div>
                    ))}
                  </div>
                </div>
              )}
              
              {/* Info operatore libero */}
              <div className="bg-green-50 rounded-lg p-3 border border-green-200">
                <p className="text-sm text-green-700 flex items-center gap-2">
                  <Sparkles className="w-4 h-4" />
                  <span>L'operatore è <strong>libero</strong> durante questo tempo!</span>
                </p>
                <p className="text-xs text-green-600 mt-1">
                  Puoi aggiungere un servizio rapido per un altro cliente.
                </p>
              </div>
              
              {/* Azioni */}
              <div className="flex gap-2 pt-2">
                <Button 
                  variant="outline" 
                  onClick={() => setPosaInfoOpen(false)}
                  className="flex-1"
                >
                  Chiudi
                </Button>
                <Button 
                  onClick={handleAddServizioDurantePosa}
                  className="flex-1 bg-green-600 hover:bg-green-700 gap-2"
                >
                  <Plus className="w-4 h-4" />
                  Aggiungi Servizio
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Dialog Storico Cliente */}
      <StoricoCliente
        clienteId={storicoClienteId}
        clienteNome={storicoClienteNome}
        open={storicoOpen}
        onOpenChange={setStoricoOpen}
        token={token}
      />
    </div>
  );
}
